// Permissions an outside assistant holds: the Ask each time / Automatic switch of a family,
// its Allowed without asking well, the connection groups inside it, the floor note and the
// plain-words answers to the server's 409s. Used by Connections › Other AI assistants
// (agent-connections/family-row.tsx, allowed-well.tsx). Action labels are keyed by tool name
// (`action_<tool>`); a tool this namespace has no label for shows the server's own label.
// Placeholders are `{name}`, filled with `formatCopy`; counts are `one:…|other:…` forms.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // The Use your connections switch (data:connections), mirroring the Connections switch copy.
  connTitle: 'Use your connections',
  connDescribe:
    'Slack, Pipedream apps, custom MCP and Postgres. Changes only on a connection set to Read & write.',
  connConfirmOn:
    'Let {app} use {company}’s connections as you? Anything that changes something waits for you in your Inbox unless you allow it.',
  connConfirmOff: 'Stop {app} from using your connections?',
  connEnabled: '{app} can now use your connections.',
  connDisabled: '{app} can no longer use your connections.',
  // The Read live stores (stores:read) and Media monitoring (media:read) switches.
  storesTitle: 'Read live stores',
  storesDescribe:
    'Live prices, stock and product details from real web shops, with Poneva’s certified store tools. Each read uses credits.',
  storesConfirmOn: 'Let {app} read live stores as you? Each read uses {company}’s credits.',
  storesConfirmOff: 'Stop {app} from reading live stores?',
  storesEnabled: '{app} can now read live stores.',
  storesDisabled: '{app} can no longer read live stores.',
  mediaTitle: 'Media monitoring',
  mediaDescribe:
    'Search and quote what was said on the podcasts and radio that Poneva’s media archive collects.',
  mediaConfirmOn: 'Let {app} search {company}’s media monitoring as you?',
  mediaConfirmOff: 'Stop {app} from searching media monitoring?',
  mediaEnabled: '{app} can now search media monitoring.',
  mediaDisabled: '{app} can no longer search media monitoring.',
  // The two modes of a permission family.
  modeAsk: 'Ask each time',
  modeAutomatic: 'Automatic',
  modeGroupLabel: '{family}: approval',
  askExplain: 'Each action waits for you in your Inbox, unless you allow it below.',
  automaticSave:
    '{app} saves work and asks Poneva without asking you, including things that can’t be undone.',
  automaticConnections:
    '{app} uses your connections without asking you, including changes on connections set to Read & write. Gmail and Outlook sends still ask you.',
  confirmAutomatic: 'Let {app} do this without asking you first?',
  modeAskSet: '{app} now asks you each time.',
  modeAutomaticSet: '{app} now does this without asking.',
  // The Allowed without asking well and its counts.
  allowedTitle: 'Allowed without asking',
  allowedOf: '{allowed} of {total}',
  allowedNone: 'None yet',
  allowedActions: 'one:action|other:actions',
  groupReadOnly: 'Set to Read, so nothing changes',
  groupSendingAsks: 'Gmail and Outlook sends still ask you',
  groupOff: 'Turned off',
  groupNothingToChange: 'Nothing it can change',
  groupUnavailable: 'Couldn’t load',
  showAll: 'Show all {count} {name} actions',
  showFewer: 'Show fewer',
  loading: 'Loading actions…',
  loadFailed: 'These actions couldn’t be loaded.',
  retry: 'Try again',
  // Notes under an action; the server sends the action flags.
  undoableNote: 'Can be undone.',
  lockedNote: 'Always asks you.',
  // The card note and plain-words 409 messages.
  floorNote:
    'Some things always ask you, whatever you choose: transferring company ownership, removing a member, making an App public, spending money beyond credits, confirming App code {app} wrote, and Gmail or Outlook sends. Allowing an action never gives {app} more than you have, and a connection set to Read stays read-only.',
  errScopeNotGranted: '{app} no longer has this access. The list shows the current state.',
  errNotAllowable: 'That action can’t be allowed without asking.',
  errReadOnly: 'That connection is set to Read, so nothing on it can be allowed.',
  errNotSaved: 'That wasn’t saved. Nothing changed.',
  errNotConfirmed: 'Couldn’t confirm that change. The list shows the current state.',
  // Native action labels, keyed by tool name.
  action_save_deck: 'Save a deck to your Library',
  action_save_document: 'Save a document to your Library',
  action_save_table: 'Save a table to your Library',
  action_save_chart: 'Save a chart to your Library',
  action_save_page: 'Save a page to your Library',
  action_add_to_poneva_files: 'Add a file to your Poneva files',
  action_save_poneva_skill_draft: 'Save a Skill draft',
  action_import_agent_skill: 'Import a Skill',
  action_evaluate_poneva_skill: 'Evaluate a Skill',
  action_ask_poneva: 'Ask Poneva in a private chat',
  action_continue_in_poneva: 'Continue in a Poneva chat',
  action_design_shopping_assistant: 'Save a shopping assistant design',
  action_storefront_preview_turn: 'Preview a shopping assistant’s answer',
  action_test_shopping_assistant: 'Test a shopping assistant with a question',
  action_fix_shopping_assistant: 'Fix a shopping assistant',
  action_check_shopping_assistant_again: 'Check a shopping assistant again',
  action_build_poneva_app: 'Build a Live App',
  action_delete_poneva_app: 'Delete a Live App',
  action_update_item_sharing: 'Change who an item is shared with',
  // Per-action notes where a flag cannot say it.
  note_ask_poneva: 'Uses credits.',
  note_storefront_preview_turn: 'Uses credits.',
  note_test_shopping_assistant: 'Uses credits.',
  note_fix_shopping_assistant: 'Uses credits.',
  note_check_shopping_assistant_again: 'Uses credits.',
  note_update_item_sharing: 'They see it right away. You can stop sharing.',
  note_delete_poneva_app: 'Can’t be undone.',
} as const;

export type AgentPermissionsCopyKey = keyof typeof en;
export type AgentPermissionsCopy = Readonly<Record<AgentPermissionsCopyKey, string>>;

const others: Record<string, Record<AgentPermissionsCopyKey, string>> = {
  bg: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Използване на вашите връзки',
    connDescribe:
      'Slack, приложения на Pipedream, собствен MCP и Postgres. Промени само върху връзка, зададена на Четене и писане.',
    connConfirmOn:
      'Да позволите ли на {app} да използва връзките на {company} като вас? Всичко, което променя нещо, ви чака във Входящата поща, освен ако не го разрешите.',
    connConfirmOff: 'Да спрете ли {app} да използва вашите връзки?',
    connEnabled: '{app} вече може да използва вашите връзки.',
    connDisabled: '{app} вече не може да използва вашите връзки.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Четене на магазини на живо',
    storesDescribe:
      'Актуални цени, наличност и данни за продукти от истински онлайн магазини чрез сертифицираните инструменти на Poneva. Всяко четене използва кредити.',
    storesConfirmOn:
      'Да позволите ли на {app} да чете магазини на живо като вас? Всяко четене използва кредитите на {company}.',
    storesConfirmOff: 'Да спрете ли {app} да чете магазини на живо?',
    storesEnabled: '{app} вече може да чете магазини на живо.',
    storesDisabled: '{app} вече не може да чете магазини на живо.',
    mediaTitle: 'Медиен мониторинг',
    mediaDescribe:
      'Търсене и цитиране на казаното в подкастите и радиото, които медийният архив на Poneva събира.',
    mediaConfirmOn:
      'Да позволите ли на {app} да търси в медийния мониторинг на {company} като вас?',
    mediaConfirmOff: 'Да спрете ли {app} да търси в медийния мониторинг?',
    mediaEnabled: '{app} вече може да търси в медийния мониторинг.',
    mediaDisabled: '{app} вече не може да търси в медийния мониторинг.',
    // The two modes of a permission family.
    modeAsk: 'Питай всеки път',
    modeAutomatic: 'Автоматично',
    modeGroupLabel: '{family}: одобрение',
    askExplain: 'Всяко действие ви чака във Входящата поща, освен ако не го разрешите по-долу.',
    automaticSave:
      '{app} запазва работа и пита Poneva, без да пита вас, включително неща, които не могат да бъдат отменени.',
    automaticConnections:
      '{app} използва вашите връзки, без да пита вас, включително за промени върху връзки, зададени на Четене и писане. Изпращанията от Gmail и Outlook пак питат вас.',
    confirmAutomatic: 'Да позволите ли на {app} да направи това, без първо да ви пита?',
    modeAskSet: '{app} вече ви пита всеки път.',
    modeAutomaticSet: '{app} вече прави това, без да пита.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Разрешено без питане',
    allowedOf: '{allowed} от {total}',
    allowedNone: 'Още няма',
    allowedActions: 'one:действие|other:действия',
    groupReadOnly: 'Зададена на Четене, така че нищо не се променя',
    groupSendingAsks: 'Изпращанията от Gmail и Outlook пак питат вас',
    groupOff: 'Изключена',
    groupNothingToChange: 'Няма какво да променя',
    groupUnavailable: 'Не можа да се зареди',
    showAll: 'Показване на всичките {count} действия в {name}',
    showFewer: 'Показване на по-малко',
    loading: 'Зареждане на действията…',
    loadFailed: 'Тези действия не можаха да се заредят.',
    retry: 'Опитайте отново',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Може да се отмени.',
    lockedNote: 'Винаги пита вас.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Някои неща винаги питат вас, независимо какво изберете: прехвърляне на собствеността на компанията, премахване на член, правене на Приложение публично, харчене на пари извън кредитите, потвърждаване на код на Приложение, написан от {app}, и изпращане от Gmail или Outlook. Разрешаването на действие никога не дава на {app} повече, отколкото имате вие, а връзка, зададена на Четене, остава само за четене.',
    errScopeNotGranted: '{app} вече няма този достъп. Списъкът показва текущото състояние.',
    errNotAllowable: 'Това действие не може да се разреши без питане.',
    errReadOnly: 'Тази връзка е зададена на Четене, затова нищо по нея не може да се разреши.',
    errNotSaved: 'Не беше запазено. Нищо не се промени.',
    errNotConfirmed: 'Промяната не можа да се потвърди. Списъкът показва текущото състояние.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Запазване на презентация във вашата Библиотека',
    action_save_document: 'Запазване на документ във вашата Библиотека',
    action_save_table: 'Запазване на таблица във вашата Библиотека',
    action_save_chart: 'Запазване на диаграма във вашата Библиотека',
    action_save_page: 'Запазване на страница във вашата Библиотека',
    action_add_to_poneva_files: 'Добавяне на файл към вашите файлове в Poneva',
    action_save_poneva_skill_draft: 'Запазване на чернова на Skill',
    action_import_agent_skill: 'Импортиране на Skill',
    action_evaluate_poneva_skill: 'Оценяване на Skill',
    action_ask_poneva: 'Попитайте Poneva в личен чат',
    action_continue_in_poneva: 'Продължаване в чат на Poneva',
    action_design_shopping_assistant: 'Запазване на дизайн на пазарски асистент',
    action_storefront_preview_turn: 'Преглед на отговор на пазарски асистент',
    action_test_shopping_assistant: 'Тестване на пазарски асистент с въпрос',
    action_fix_shopping_assistant: 'Поправяне на пазарски асистент',
    action_check_shopping_assistant_again: 'Повторна проверка на пазарски асистент',
    action_build_poneva_app: 'Създаване на Live App',
    action_delete_poneva_app: 'Изтриване на Live App',
    action_update_item_sharing: 'Промяна на това с кого е споделен елемент',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Използва кредити.',
    note_storefront_preview_turn: 'Използва кредити.',
    note_test_shopping_assistant: 'Използва кредити.',
    note_fix_shopping_assistant: 'Използва кредити.',
    note_check_shopping_assistant_again: 'Използва кредити.',
    note_update_item_sharing: 'Те го виждат веднага. Можете да спрете споделянето.',
    note_delete_poneva_app: 'Не може да се отмени.',
  },
  cs: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Používat vaše připojení',
    connDescribe:
      'Slack, aplikace Pipedream, vlastní MCP a Postgres. Změny jen na připojení nastaveném na Čtení a zápis.',
    connConfirmOn:
      'Nechat {app} používat připojení firmy {company} jako vás? Vše, co něco mění, na vás čeká ve Schránce, pokud to nepovolíte.',
    connConfirmOff: 'Zakázat {app} používat vaše připojení?',
    connEnabled: '{app} teď může používat vaše připojení.',
    connDisabled: '{app} už nemůže používat vaše připojení.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Čtení obchodů naživo',
    storesDescribe:
      'Aktuální ceny, skladové zásoby a údaje o produktech ze skutečných e-shopů pomocí certifikovaných nástrojů Poneva. Každé čtení spotřebuje kredity.',
    storesConfirmOn:
      'Povolit aplikaci {app} číst obchody naživo vaším jménem? Každé čtení spotřebuje kredity společnosti {company}.',
    storesConfirmOff: 'Zakázat aplikaci {app} čtení obchodů naživo?',
    storesEnabled: '{app} nyní může číst obchody naživo.',
    storesDisabled: '{app} už nemůže číst obchody naživo.',
    mediaTitle: 'Monitoring médií',
    mediaDescribe:
      'Vyhledávání a citace toho, co zaznělo v podcastech a rádiu, které shromažďuje mediální archiv Poneva.',
    mediaConfirmOn:
      'Povolit aplikaci {app} prohledávat monitoring médií společnosti {company} vaším jménem?',
    mediaConfirmOff: 'Zakázat aplikaci {app} prohledávání monitoringu médií?',
    mediaEnabled: '{app} nyní může prohledávat monitoring médií.',
    mediaDisabled: '{app} už nemůže prohledávat monitoring médií.',
    // The two modes of a permission family.
    modeAsk: 'Zeptat se pokaždé',
    modeAutomatic: 'Automaticky',
    modeGroupLabel: '{family}: schvalování',
    askExplain: 'Každá akce na vás čeká ve Schránce, pokud ji nepovolíte níže.',
    automaticSave:
      '{app} ukládá práci a ptá se Ponevy, aniž by se zeptal vás, včetně věcí, které nejdou vrátit zpět.',
    automaticConnections:
      '{app} používá vaše připojení, aniž by se zeptal vás, včetně změn na připojeních nastavených na Čtení a zápis. Odesílání z Gmailu a Outlooku se stále ptá vás.',
    confirmAutomatic: 'Nechat {app} udělat to bez předchozího dotazu?',
    modeAskSet: '{app} se vás teď ptá pokaždé.',
    modeAutomaticSet: '{app} to teď dělá bez dotazu.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Povoleno bez dotazu',
    allowedOf: '{allowed} ze {total}',
    allowedNone: 'Zatím žádné',
    allowedActions: 'one:akce|few:akce|many:akce|other:akcí',
    groupReadOnly: 'Nastaveno na Čtení, takže se nic nemění',
    groupSendingAsks: 'Odesílání z Gmailu a Outlooku se stále ptá vás',
    groupOff: 'Vypnuto',
    groupNothingToChange: 'Nemá co měnit',
    groupUnavailable: 'Nepodařilo se načíst',
    showAll: 'Zobrazit všech {count} akcí v {name}',
    showFewer: 'Zobrazit méně',
    loading: 'Načítání akcí…',
    loadFailed: 'Tyto akce se nepodařilo načíst.',
    retry: 'Zkusit znovu',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Jde vrátit zpět.',
    lockedNote: 'Vždy se ptá vás.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Některé věci se vždy ptají vás, ať zvolíte cokoli: převod vlastnictví firmy, odebrání člena, zveřejnění Aplikace, utrácení peněz nad rámec kreditů, potvrzení kódu Aplikace, který napsal {app}, a odesílání z Gmailu či Outlooku. Povolení akce nikdy nedá {app} víc, než máte vy, a připojení nastavené na Čtení zůstává jen pro čtení.',
    errScopeNotGranted: '{app} už tento přístup nemá. Seznam ukazuje aktuální stav.',
    errNotAllowable: 'Tuto akci nelze povolit bez dotazu.',
    errReadOnly: 'Toto připojení je nastaveno na Čtení, takže na něm nelze nic povolit.',
    errNotSaved: 'Nebylo to uloženo. Nic se nezměnilo.',
    errNotConfirmed: 'Tuto změnu se nepodařilo potvrdit. Seznam ukazuje aktuální stav.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Uložit prezentaci do Knihovny',
    action_save_document: 'Uložit dokument do Knihovny',
    action_save_table: 'Uložit tabulku do Knihovny',
    action_save_chart: 'Uložit graf do Knihovny',
    action_save_page: 'Uložit stránku do Knihovny',
    action_add_to_poneva_files: 'Přidat soubor mezi soubory Poneva',
    action_save_poneva_skill_draft: 'Uložit koncept Skillu',
    action_import_agent_skill: 'Importovat Skill',
    action_evaluate_poneva_skill: 'Vyhodnotit Skill',
    action_ask_poneva: 'Zeptat se Ponevy v soukromém chatu',
    action_continue_in_poneva: 'Pokračovat v chatu Poneva',
    action_design_shopping_assistant: 'Uložit návrh nákupního asistenta',
    action_storefront_preview_turn: 'Náhled odpovědi nákupního asistenta',
    action_test_shopping_assistant: 'Otestovat nákupního asistenta otázkou',
    action_fix_shopping_assistant: 'Opravit nákupního asistenta',
    action_check_shopping_assistant_again: 'Znovu zkontrolovat nákupního asistenta',
    action_build_poneva_app: 'Vytvořit Live App',
    action_delete_poneva_app: 'Smazat Live App',
    action_update_item_sharing: 'Změnit, s kým je položka sdílena',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Spotřebovává kredity.',
    note_storefront_preview_turn: 'Spotřebovává kredity.',
    note_test_shopping_assistant: 'Spotřebovává kredity.',
    note_fix_shopping_assistant: 'Spotřebovává kredity.',
    note_check_shopping_assistant_again: 'Spotřebovává kredity.',
    note_update_item_sharing: 'Vidí to hned. Sdílení můžete ukončit.',
    note_delete_poneva_app: 'Nejde vrátit zpět.',
  },
  da: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Brug dine forbindelser',
    connDescribe:
      'Slack, Pipedream-apps, egen MCP og Postgres. Ændringer sker kun på en forbindelse, der er sat til Læsning og skrivning.',
    connConfirmOn:
      'Lade {app} bruge {company}s forbindelser som dig? Alt, der ændrer noget, venter på dig i Indbakken, medmindre du tillader det.',
    connConfirmOff: 'Stoppe {app} i at bruge dine forbindelser?',
    connEnabled: '{app} kan nu bruge dine forbindelser.',
    connDisabled: '{app} kan ikke længere bruge dine forbindelser.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Læs butikker live',
    storesDescribe:
      'Aktuelle priser, lagerstatus og produktdetaljer fra rigtige webshops med Ponevas certificerede butiksværktøjer. Hver læsning bruger kreditter.',
    storesConfirmOn:
      'Lad {app} læse butikker live som dig? Hver læsning bruger {company}s kreditter.',
    storesConfirmOff: 'Stop {app} i at læse butikker live?',
    storesEnabled: '{app} kan nu læse butikker live.',
    storesDisabled: '{app} kan ikke længere læse butikker live.',
    mediaTitle: 'Medieovervågning',
    mediaDescribe:
      'Søg i og citér, hvad der blev sagt i de podcasts og radioprogrammer, som Ponevas mediearkiv indsamler.',
    mediaConfirmOn: 'Lad {app} søge i {company}s medieovervågning som dig?',
    mediaConfirmOff: 'Stop {app} i at søge i medieovervågningen?',
    mediaEnabled: '{app} kan nu søge i medieovervågningen.',
    mediaDisabled: '{app} kan ikke længere søge i medieovervågningen.',
    // The two modes of a permission family.
    modeAsk: 'Spørg hver gang',
    modeAutomatic: 'Automatisk',
    modeGroupLabel: '{family}: godkendelse',
    askExplain: 'Hver handling venter på dig i Indbakken, medmindre du tillader den nedenfor.',
    automaticSave:
      '{app} gemmer arbejde og spørger Poneva uden at spørge dig, også ting der ikke kan fortrydes.',
    automaticConnections:
      '{app} bruger dine forbindelser uden at spørge dig, også til ændringer på forbindelser sat til Læsning og skrivning. Afsendelse fra Gmail og Outlook spørger stadig dig.',
    confirmAutomatic: 'Lade {app} gøre dette uden først at spørge dig?',
    modeAskSet: '{app} spørger dig nu hver gang.',
    modeAutomaticSet: '{app} gør nu dette uden at spørge.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Tilladt uden at spørge',
    allowedOf: '{allowed} af {total}',
    allowedNone: 'Ingen endnu',
    allowedActions: 'one:handling|other:handlinger',
    groupReadOnly: 'Sat til Læsning, så intet ændres',
    groupSendingAsks: 'Afsendelse fra Gmail og Outlook spørger stadig dig',
    groupOff: 'Slået fra',
    groupNothingToChange: 'Intet at ændre',
    groupUnavailable: 'Kunne ikke indlæses',
    showAll: 'Vis alle {count} handlinger i {name}',
    showFewer: 'Vis færre',
    loading: 'Indlæser handlinger …',
    loadFailed: 'Handlingerne kunne ikke indlæses.',
    retry: 'Prøv igen',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Kan fortrydes.',
    lockedNote: 'Spørger altid dig.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Nogle ting spørger altid dig, uanset hvad du vælger: overdragelse af virksomhedens ejerskab, fjernelse af et medlem, at gøre en App offentlig, at bruge penge ud over kreditter, at bekræfte App-kode som {app} har skrevet og afsendelse fra Gmail eller Outlook. At tillade en handling giver aldrig {app} mere, end du har, og en forbindelse sat til Læsning forbliver skrivebeskyttet.',
    errScopeNotGranted: '{app} har ikke længere denne adgang. Listen viser den aktuelle tilstand.',
    errNotAllowable: 'Den handling kan ikke tillades uden at spørge.',
    errReadOnly: 'Den forbindelse er sat til Læsning, så intet på den kan tillades.',
    errNotSaved: 'Det blev ikke gemt. Intet ændrede sig.',
    errNotConfirmed: 'Ændringen kunne ikke bekræftes. Listen viser den aktuelle tilstand.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Gem en præsentation i dit Bibliotek',
    action_save_document: 'Gem et dokument i dit Bibliotek',
    action_save_table: 'Gem en tabel i dit Bibliotek',
    action_save_chart: 'Gem et diagram i dit Bibliotek',
    action_save_page: 'Gem en side i dit Bibliotek',
    action_add_to_poneva_files: 'Tilføj en fil til dine Poneva-filer',
    action_save_poneva_skill_draft: 'Gem et udkast til en Skill',
    action_import_agent_skill: 'Importér en Skill',
    action_evaluate_poneva_skill: 'Evaluér en Skill',
    action_ask_poneva: 'Spørg Poneva i en privat chat',
    action_continue_in_poneva: 'Fortsæt i en Poneva-chat',
    action_design_shopping_assistant: 'Gem et design til en shoppingassistent',
    action_storefront_preview_turn: 'Se et eksempel på en shoppingassistents svar',
    action_test_shopping_assistant: 'Test en shoppingassistent med et spørgsmål',
    action_fix_shopping_assistant: 'Ret en shoppingassistent',
    action_check_shopping_assistant_again: 'Tjek en shoppingassistent igen',
    action_build_poneva_app: 'Byg en Live App',
    action_delete_poneva_app: 'Slet en Live App',
    action_update_item_sharing: 'Ændr, hvem et emne deles med',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Bruger kreditter.',
    note_storefront_preview_turn: 'Bruger kreditter.',
    note_test_shopping_assistant: 'Bruger kreditter.',
    note_fix_shopping_assistant: 'Bruger kreditter.',
    note_check_shopping_assistant_again: 'Bruger kreditter.',
    note_update_item_sharing: 'De ser det med det samme. Du kan stoppe delingen.',
    note_delete_poneva_app: 'Kan ikke fortrydes.',
  },
  de: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Ihre Verbindungen nutzen',
    connDescribe:
      'Slack, Pipedream-Apps, eigenes MCP und Postgres. Änderungen nur bei einer Verbindung, die auf Lesen und Schreiben steht.',
    connConfirmOn:
      'Soll {app} die Verbindungen von {company} wie Sie nutzen? Alles, was etwas ändert, wartet in Ihrem Posteingang auf Sie, sofern Sie es nicht erlauben.',
    connConfirmOff: 'Soll {app} Ihre Verbindungen nicht mehr nutzen?',
    connEnabled: '{app} kann jetzt Ihre Verbindungen nutzen.',
    connDisabled: '{app} kann Ihre Verbindungen nicht mehr nutzen.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Shops live lesen',
    storesDescribe:
      'Aktuelle Preise, Lagerbestand und Produktdetails aus echten Onlineshops mit den zertifizierten Shop-Tools von Poneva. Jeder Abruf verbraucht Credits.',
    storesConfirmOn:
      '{app} erlauben, Shops live in Ihrem Namen zu lesen? Jeder Abruf verbraucht Credits von {company}.',
    storesConfirmOff: '{app} das Live-Lesen von Shops verbieten?',
    storesEnabled: '{app} kann jetzt Shops live lesen.',
    storesDisabled: '{app} kann keine Shops mehr live lesen.',
    mediaTitle: 'Medienbeobachtung',
    mediaDescribe:
      'Durchsuchen und zitieren, was in den Podcasts und Radiosendungen gesagt wurde, die das Medienarchiv von Poneva sammelt.',
    mediaConfirmOn:
      '{app} erlauben, die Medienbeobachtung von {company} in Ihrem Namen zu durchsuchen?',
    mediaConfirmOff: '{app} das Durchsuchen der Medienbeobachtung verbieten?',
    mediaEnabled: '{app} kann jetzt die Medienbeobachtung durchsuchen.',
    mediaDisabled: '{app} kann die Medienbeobachtung nicht mehr durchsuchen.',
    // The two modes of a permission family.
    modeAsk: 'Jedes Mal fragen',
    modeAutomatic: 'Automatisch',
    modeGroupLabel: '{family}: Freigabe',
    askExplain:
      'Jede Aktion wartet in Ihrem Posteingang auf Sie, sofern Sie sie unten nicht erlauben.',
    automaticSave:
      '{app} speichert Arbeit und fragt Poneva, ohne Sie zu fragen, auch Dinge, die sich nicht rückgängig machen lassen.',
    automaticConnections:
      '{app} nutzt Ihre Verbindungen, ohne Sie zu fragen, auch für Änderungen an Verbindungen, die auf Lesen und Schreiben stehen. Das Senden über Gmail und Outlook fragt weiterhin Sie.',
    confirmAutomatic: 'Soll {app} das tun, ohne vorher zu fragen?',
    modeAskSet: '{app} fragt Sie jetzt jedes Mal.',
    modeAutomaticSet: '{app} tut das jetzt ohne Nachfrage.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Ohne Nachfrage erlaubt',
    allowedOf: '{allowed} von {total}',
    allowedNone: 'Noch keine',
    allowedActions: 'one:Aktion|other:Aktionen',
    groupReadOnly: 'Auf Lesen gestellt, daher ändert sich nichts',
    groupSendingAsks: 'Senden über Gmail und Outlook fragt weiterhin Sie',
    groupOff: 'Ausgeschaltet',
    groupNothingToChange: 'Nichts, was es ändern kann',
    groupUnavailable: 'Konnte nicht geladen werden',
    showAll: 'Alle {count} {name}-Aktionen anzeigen',
    showFewer: 'Weniger anzeigen',
    loading: 'Aktionen werden geladen …',
    loadFailed: 'Die Aktionen konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Lässt sich rückgängig machen.',
    lockedNote: 'Fragt immer Sie.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Manches fragt immer Sie, egal was Sie wählen: die Übertragung des Firmeneigentums, das Entfernen eines Mitglieds, eine App öffentlich machen, Geld über Credits hinaus ausgeben, App-Code bestätigen, den {app} geschrieben hat, sowie Gmail- oder Outlook-Versand. Eine Aktion zu erlauben gibt {app} nie mehr, als Sie haben, und eine Verbindung auf Lesen bleibt schreibgeschützt.',
    errScopeNotGranted: '{app} hat diesen Zugriff nicht mehr. Die Liste zeigt den aktuellen Stand.',
    errNotAllowable: 'Diese Aktion kann nicht ohne Nachfrage erlaubt werden.',
    errReadOnly: 'Diese Verbindung steht auf Lesen, daher kann dort nichts erlaubt werden.',
    errNotSaved: 'Das wurde nicht gespeichert. Nichts hat sich geändert.',
    errNotConfirmed:
      'Die Änderung konnte nicht bestätigt werden. Die Liste zeigt den aktuellen Stand.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Eine Präsentation in Ihrer Bibliothek speichern',
    action_save_document: 'Ein Dokument in Ihrer Bibliothek speichern',
    action_save_table: 'Eine Tabelle in Ihrer Bibliothek speichern',
    action_save_chart: 'Ein Diagramm in Ihrer Bibliothek speichern',
    action_save_page: 'Eine Seite in Ihrer Bibliothek speichern',
    action_add_to_poneva_files: 'Eine Datei zu Ihren Poneva-Dateien hinzufügen',
    action_save_poneva_skill_draft: 'Den Entwurf eines Skills speichern',
    action_import_agent_skill: 'Einen Skill importieren',
    action_evaluate_poneva_skill: 'Einen Skill bewerten',
    action_ask_poneva: 'Poneva in einem privaten Chat fragen',
    action_continue_in_poneva: 'In einem Poneva-Chat weitermachen',
    action_design_shopping_assistant: 'Das Design eines Shopping-Assistenten speichern',
    action_storefront_preview_turn: 'Antwort eines Shopping-Assistenten in der Vorschau ansehen',
    action_test_shopping_assistant: 'Einen Shopping-Assistenten mit einer Frage testen',
    action_fix_shopping_assistant: 'Einen Shopping-Assistenten reparieren',
    action_check_shopping_assistant_again: 'Einen Shopping-Assistenten erneut prüfen',
    action_build_poneva_app: 'Eine Live-App bauen',
    action_delete_poneva_app: 'Eine Live-App löschen',
    action_update_item_sharing: 'Ändern, mit wem etwas geteilt wird',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Verbraucht Credits.',
    note_storefront_preview_turn: 'Verbraucht Credits.',
    note_test_shopping_assistant: 'Verbraucht Credits.',
    note_fix_shopping_assistant: 'Verbraucht Credits.',
    note_check_shopping_assistant_again: 'Verbraucht Credits.',
    note_update_item_sharing: 'Die anderen sehen es sofort. Sie können das Teilen beenden.',
    note_delete_poneva_app: 'Lässt sich nicht rückgängig machen.',
  },
  el: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Χρήση των συνδέσεών σας',
    connDescribe:
      'Slack, εφαρμογές Pipedream, δικό σας MCP και Postgres. Αλλαγές μόνο σε σύνδεση που έχει οριστεί σε Ανάγνωση και εγγραφή.',
    connConfirmOn:
      'Να επιτρέψετε στο {app} να χρησιμοποιεί τις συνδέσεις της {company} ως εσάς; Ό,τι αλλάζει κάτι σάς περιμένει στα Εισερχόμενα, εκτός αν το επιτρέψετε.',
    connConfirmOff: 'Να σταματήσει το {app} να χρησιμοποιεί τις συνδέσεις σας;',
    connEnabled: 'Το {app} μπορεί τώρα να χρησιμοποιεί τις συνδέσεις σας.',
    connDisabled: 'Το {app} δεν μπορεί πλέον να χρησιμοποιεί τις συνδέσεις σας.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Ανάγνωση καταστημάτων σε πραγματικό χρόνο',
    storesDescribe:
      'Τρέχουσες τιμές, απόθεμα και στοιχεία προϊόντων από πραγματικά ηλεκτρονικά καταστήματα με τα πιστοποιημένα εργαλεία της Poneva. Κάθε ανάγνωση χρησιμοποιεί μονάδες.',
    storesConfirmOn:
      'Να επιτρέπεται στο {app} να διαβάζει καταστήματα σε πραγματικό χρόνο ως εσείς; Κάθε ανάγνωση χρησιμοποιεί μονάδες της {company}.',
    storesConfirmOff: 'Να σταματήσει το {app} να διαβάζει καταστήματα σε πραγματικό χρόνο;',
    storesEnabled: 'Το {app} μπορεί πλέον να διαβάζει καταστήματα σε πραγματικό χρόνο.',
    storesDisabled: 'Το {app} δεν μπορεί πλέον να διαβάζει καταστήματα σε πραγματικό χρόνο.',
    mediaTitle: 'Παρακολούθηση μέσων',
    mediaDescribe:
      'Αναζήτηση και παράθεση όσων ειπώθηκαν στα podcast και στο ραδιόφωνο που συλλέγει το αρχείο μέσων της Poneva.',
    mediaConfirmOn:
      'Να επιτρέπεται στο {app} να αναζητά στην παρακολούθηση μέσων της {company} ως εσείς;',
    mediaConfirmOff: 'Να σταματήσει το {app} να αναζητά στην παρακολούθηση μέσων;',
    mediaEnabled: 'Το {app} μπορεί πλέον να αναζητά στην παρακολούθηση μέσων.',
    mediaDisabled: 'Το {app} δεν μπορεί πλέον να αναζητά στην παρακολούθηση μέσων.',
    // The two modes of a permission family.
    modeAsk: 'Ρώτα κάθε φορά',
    modeAutomatic: 'Αυτόματα',
    modeGroupLabel: '{family}: έγκριση',
    askExplain: 'Κάθε ενέργεια σάς περιμένει στα Εισερχόμενα, εκτός αν την επιτρέψετε παρακάτω.',
    automaticSave:
      'Το {app} αποθηκεύει εργασία και ρωτά την Poneva χωρίς να σας ρωτήσει, ακόμα και για πράγματα που δεν αναιρούνται.',
    automaticConnections:
      'Το {app} χρησιμοποιεί τις συνδέσεις σας χωρίς να σας ρωτήσει, ακόμα και για αλλαγές σε συνδέσεις που έχουν οριστεί σε Ανάγνωση και εγγραφή. Οι αποστολές από Gmail και Outlook σάς ρωτούν ακόμα.',
    confirmAutomatic: 'Να το κάνει το {app} χωρίς να σας ρωτήσει πρώτα;',
    modeAskSet: 'Το {app} τώρα σας ρωτά κάθε φορά.',
    modeAutomaticSet: 'Το {app} τώρα το κάνει χωρίς να ρωτήσει.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Επιτρέπονται χωρίς ερώτηση',
    allowedOf: '{allowed} από {total}',
    allowedNone: 'Καμία ακόμα',
    allowedActions: 'one:ενέργεια|other:ενέργειες',
    groupReadOnly: 'Ορισμένη σε Ανάγνωση, άρα δεν αλλάζει τίποτα',
    groupSendingAsks: 'Οι αποστολές από Gmail και Outlook σάς ρωτούν ακόμα',
    groupOff: 'Απενεργοποιημένη',
    groupNothingToChange: 'Δεν έχει τίποτα να αλλάξει',
    groupUnavailable: 'Δεν φορτώθηκε',
    showAll: 'Εμφάνιση και των {count} ενεργειών του {name}',
    showFewer: 'Εμφάνιση λιγότερων',
    loading: 'Φόρτωση ενεργειών…',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση αυτών των ενεργειών.',
    retry: 'Δοκιμάστε ξανά',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Μπορεί να αναιρεθεί.',
    lockedNote: 'Σας ρωτά πάντα.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Ορισμένα πράγματα σας ρωτούν πάντα, ό,τι κι αν επιλέξετε: μεταβίβαση της ιδιοκτησίας της εταιρείας, αφαίρεση μέλους, δημοσιοποίηση μιας Εφαρμογής, δαπάνη χρημάτων πέρα από τα credits, επιβεβαίωση κώδικα Εφαρμογής που έγραψε το {app} και αποστολές από Gmail ή Outlook. Η έγκριση μιας ενέργειας δεν δίνει ποτέ στο {app} περισσότερα από όσα έχετε εσείς και μια σύνδεση σε Ανάγνωση παραμένει μόνο για ανάγνωση.',
    errScopeNotGranted:
      'Το {app} δεν έχει πλέον αυτή την πρόσβαση. Η λίστα δείχνει την τρέχουσα κατάσταση.',
    errNotAllowable: 'Αυτή η ενέργεια δεν μπορεί να επιτραπεί χωρίς ερώτηση.',
    errReadOnly: 'Αυτή η σύνδεση είναι σε Ανάγνωση, άρα δεν μπορεί να επιτραπεί τίποτα σε αυτήν.',
    errNotSaved: 'Δεν αποθηκεύτηκε. Τίποτα δεν άλλαξε.',
    errNotConfirmed:
      'Δεν ήταν δυνατή η επιβεβαίωση της αλλαγής. Η λίστα δείχνει την τρέχουσα κατάσταση.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Αποθήκευση παρουσίασης στη Βιβλιοθήκη σας',
    action_save_document: 'Αποθήκευση εγγράφου στη Βιβλιοθήκη σας',
    action_save_table: 'Αποθήκευση πίνακα στη Βιβλιοθήκη σας',
    action_save_chart: 'Αποθήκευση γραφήματος στη Βιβλιοθήκη σας',
    action_save_page: 'Αποθήκευση σελίδας στη Βιβλιοθήκη σας',
    action_add_to_poneva_files: 'Προσθήκη αρχείου στα αρχεία σας στην Poneva',
    action_save_poneva_skill_draft: 'Αποθήκευση πρόχειρου ενός Skill',
    action_import_agent_skill: 'Εισαγωγή ενός Skill',
    action_evaluate_poneva_skill: 'Αξιολόγηση ενός Skill',
    action_ask_poneva: 'Ρωτήστε την Poneva σε ιδιωτική συνομιλία',
    action_continue_in_poneva: 'Συνέχεια σε συνομιλία της Poneva',
    action_design_shopping_assistant: 'Αποθήκευση σχεδίου βοηθού αγορών',
    action_storefront_preview_turn: 'Προεπισκόπηση απάντησης βοηθού αγορών',
    action_test_shopping_assistant: 'Δοκιμή βοηθού αγορών με μια ερώτηση',
    action_fix_shopping_assistant: 'Διόρθωση βοηθού αγορών',
    action_check_shopping_assistant_again: 'Νέος έλεγχος βοηθού αγορών',
    action_build_poneva_app: 'Δημιουργία Live App',
    action_delete_poneva_app: 'Διαγραφή Live App',
    action_update_item_sharing: 'Αλλαγή των ατόμων με τα οποία κοινοποιείται ένα στοιχείο',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Χρησιμοποιεί credits.',
    note_storefront_preview_turn: 'Χρησιμοποιεί credits.',
    note_test_shopping_assistant: 'Χρησιμοποιεί credits.',
    note_fix_shopping_assistant: 'Χρησιμοποιεί credits.',
    note_check_shopping_assistant_again: 'Χρησιμοποιεί credits.',
    note_update_item_sharing: 'Το βλέπουν αμέσως. Μπορείτε να σταματήσετε την κοινοποίηση.',
    note_delete_poneva_app: 'Δεν μπορεί να αναιρεθεί.',
  },
  es: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Usar tus conexiones',
    connDescribe:
      'Slack, apps de Pipedream, MCP propio y Postgres. Los cambios solo se hacen en una conexión configurada en Lectura y escritura.',
    connConfirmOn:
      '¿Dejar que {app} use las conexiones de {company} como tú? Todo lo que cambie algo te espera en tu Bandeja de entrada, salvo que lo permitas.',
    connConfirmOff: '¿Impedir que {app} use tus conexiones?',
    connEnabled: '{app} ya puede usar tus conexiones.',
    connDisabled: '{app} ya no puede usar tus conexiones.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Leer tiendas en directo',
    storesDescribe:
      'Precios, existencias y detalles de productos actuales de tiendas online reales, con las herramientas certificadas de Poneva. Cada lectura usa créditos.',
    storesConfirmOn:
      '¿Permitir que {app} lea tiendas en directo en tu nombre? Cada lectura usa créditos de {company}.',
    storesConfirmOff: '¿Impedir que {app} lea tiendas en directo?',
    storesEnabled: '{app} ya puede leer tiendas en directo.',
    storesDisabled: '{app} ya no puede leer tiendas en directo.',
    mediaTitle: 'Seguimiento de medios',
    mediaDescribe:
      'Busca y cita lo que se dijo en los pódcasts y la radio que recopila el archivo de medios de Poneva.',
    mediaConfirmOn:
      '¿Permitir que {app} busque en el seguimiento de medios de {company} en tu nombre?',
    mediaConfirmOff: '¿Impedir que {app} busque en el seguimiento de medios?',
    mediaEnabled: '{app} ya puede buscar en el seguimiento de medios.',
    mediaDisabled: '{app} ya no puede buscar en el seguimiento de medios.',
    // The two modes of a permission family.
    modeAsk: 'Preguntar cada vez',
    modeAutomatic: 'Automático',
    modeGroupLabel: '{family}: aprobación',
    askExplain: 'Cada acción te espera en tu Bandeja de entrada, salvo que la permitas abajo.',
    automaticSave:
      '{app} guarda trabajo y pregunta a Poneva sin preguntarte, incluso cosas que no se pueden deshacer.',
    automaticConnections:
      '{app} usa tus conexiones sin preguntarte, incluidos los cambios en conexiones configuradas en Lectura y escritura. Los envíos de Gmail y Outlook te lo siguen preguntando.',
    confirmAutomatic: '¿Dejar que {app} haga esto sin preguntarte antes?',
    modeAskSet: '{app} ahora te pregunta cada vez.',
    modeAutomaticSet: '{app} ahora hace esto sin preguntar.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Permitido sin preguntar',
    allowedOf: '{allowed} de {total}',
    allowedNone: 'Ninguna todavía',
    allowedActions: 'one:acción|other:acciones',
    groupReadOnly: 'Configurada en Lectura, así que no cambia nada',
    groupSendingAsks: 'Los envíos de Gmail y Outlook te lo siguen preguntando',
    groupOff: 'Desactivada',
    groupNothingToChange: 'No tiene nada que cambiar',
    groupUnavailable: 'No se pudo cargar',
    showAll: 'Mostrar las {count} acciones de {name}',
    showFewer: 'Mostrar menos',
    loading: 'Cargando acciones…',
    loadFailed: 'No se pudieron cargar estas acciones.',
    retry: 'Reintentar',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Se puede deshacer.',
    lockedNote: 'Siempre te lo pregunta.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Algunas cosas siempre te lo preguntan, elijas lo que elijas: transferir la propiedad de la empresa, quitar a un miembro, hacer pública una App, gastar dinero más allá de los créditos, confirmar el código de App que escribió {app} y los envíos de Gmail u Outlook. Permitir una acción nunca da a {app} más de lo que tú tienes, y una conexión en Lectura sigue siendo de solo lectura.',
    errScopeNotGranted: '{app} ya no tiene este acceso. La lista muestra el estado actual.',
    errNotAllowable: 'Esa acción no se puede permitir sin preguntar.',
    errReadOnly: 'Esa conexión está en Lectura, así que no se puede permitir nada en ella.',
    errNotSaved: 'No se guardó. No cambió nada.',
    errNotConfirmed: 'No se pudo confirmar ese cambio. La lista muestra el estado actual.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Guardar una presentación en tu Biblioteca',
    action_save_document: 'Guardar un documento en tu Biblioteca',
    action_save_table: 'Guardar una tabla en tu Biblioteca',
    action_save_chart: 'Guardar un gráfico en tu Biblioteca',
    action_save_page: 'Guardar una página en tu Biblioteca',
    action_add_to_poneva_files: 'Añadir un archivo a tus archivos de Poneva',
    action_save_poneva_skill_draft: 'Guardar el borrador de un Skill',
    action_import_agent_skill: 'Importar un Skill',
    action_evaluate_poneva_skill: 'Evaluar un Skill',
    action_ask_poneva: 'Preguntar a Poneva en un chat privado',
    action_continue_in_poneva: 'Continuar en un chat de Poneva',
    action_design_shopping_assistant: 'Guardar el diseño de un asistente de compras',
    action_storefront_preview_turn: 'Previsualizar la respuesta de un asistente de compras',
    action_test_shopping_assistant: 'Probar un asistente de compras con una pregunta',
    action_fix_shopping_assistant: 'Arreglar un asistente de compras',
    action_check_shopping_assistant_again: 'Volver a comprobar un asistente de compras',
    action_build_poneva_app: 'Crear una Live App',
    action_delete_poneva_app: 'Eliminar una Live App',
    action_update_item_sharing: 'Cambiar con quién se comparte un elemento',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Usa créditos.',
    note_storefront_preview_turn: 'Usa créditos.',
    note_test_shopping_assistant: 'Usa créditos.',
    note_fix_shopping_assistant: 'Usa créditos.',
    note_check_shopping_assistant_again: 'Usa créditos.',
    note_update_item_sharing: 'Lo ven al instante. Puedes dejar de compartir.',
    note_delete_poneva_app: 'No se puede deshacer.',
  },
  et: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Sinu ühenduste kasutamine',
    connDescribe:
      'Slack, Pipedreami rakendused, oma MCP ja Postgres. Muudatused ainult ühendusel, mis on seatud valikule Lugemine ja kirjutamine.',
    connConfirmOn:
      'Kas lubada {app}-il kasutada ettevõtte {company} ühendusi sinu nimel? Kõik, mis midagi muudab, ootab sind Sisendkaustas, kui sa seda ei luba.',
    connConfirmOff: 'Kas keelata {app}-il sinu ühenduste kasutamine?',
    connEnabled: '{app} saab nüüd sinu ühendusi kasutada.',
    connDisabled: '{app} ei saa enam sinu ühendusi kasutada.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Poodide lugemine reaalajas',
    storesDescribe:
      'Ajakohased hinnad, laoseis ja tooteandmed päris veebipoodidest Poneva sertifitseeritud poetööriistadega. Iga lugemine kasutab krediiti.',
    storesConfirmOn:
      'Kas lubada rakendusel {app} lugeda poode reaalajas sinu nimel? Iga lugemine kasutab ettevõtte {company} krediiti.',
    storesConfirmOff: 'Kas keelata rakendusel {app} poodide reaalajas lugemine?',
    storesEnabled: '{app} saab nüüd poode reaalajas lugeda.',
    storesDisabled: '{app} ei saa enam poode reaalajas lugeda.',
    mediaTitle: 'Meediaseire',
    mediaDescribe:
      'Otsi ja tsiteeri, mida räägiti taskuhäälingutes ja raadios, mida Poneva meediaarhiiv kogub.',
    mediaConfirmOn:
      'Kas lubada rakendusel {app} otsida ettevõtte {company} meediaseirest sinu nimel?',
    mediaConfirmOff: 'Kas keelata rakendusel {app} meediaseirest otsimine?',
    mediaEnabled: '{app} saab nüüd meediaseirest otsida.',
    mediaDisabled: '{app} ei saa enam meediaseirest otsida.',
    // The two modes of a permission family.
    modeAsk: 'Küsi iga kord',
    modeAutomatic: 'Automaatne',
    modeGroupLabel: '{family}: kinnitamine',
    askExplain: 'Iga toiming ootab sind Sisendkaustas, kui sa seda allpool ei luba.',
    automaticSave:
      '{app} salvestab töid ja küsib Ponevalt sinult küsimata, sealhulgas asju, mida ei saa tagasi võtta.',
    automaticConnections:
      '{app} kasutab sinu ühendusi sinult küsimata, sealhulgas muudatusteks ühendustel, mis on seatud valikule Lugemine ja kirjutamine. Gmaili ja Outlooki saatmised küsivad endiselt sinult.',
    confirmAutomatic: 'Kas lubada {app}-il seda teha, ilma et ta enne küsiks?',
    modeAskSet: '{app} küsib nüüd sinult iga kord.',
    modeAutomaticSet: '{app} teeb seda nüüd küsimata.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Lubatud küsimata',
    allowedOf: '{allowed} / {total}',
    allowedNone: 'Veel pole',
    allowedActions: 'one:toiming|other:toimingut',
    groupReadOnly: 'Seatud valikule Lugemine, seega miski ei muutu',
    groupSendingAsks: 'Gmaili ja Outlooki saatmised küsivad endiselt sinult',
    groupOff: 'Välja lülitatud',
    groupNothingToChange: 'Pole midagi muuta',
    groupUnavailable: 'Laadimine ebaõnnestus',
    showAll: 'Näita kõiki {count} toimingut: {name}',
    showFewer: 'Näita vähem',
    loading: 'Toimingute laadimine…',
    loadFailed: 'Neid toiminguid ei õnnestunud laadida.',
    retry: 'Proovi uuesti',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Saab tagasi võtta.',
    lockedNote: 'Küsib alati sinult.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Mõned asjad küsivad alati sinult, olenemata sinu valikust: ettevõtte omandi üleandmine, liikme eemaldamine, Rakenduse avalikuks tegemine, krediitidest kaugemale kulutamine, {app}-i kirjutatud Rakenduse koodi kinnitamine ning Gmaili või Outlooki saatmised. Toimingu lubamine ei anna {app}-ile kunagi rohkem, kui sinul endal on, ja valikule Lugemine seatud ühendus jääb kirjutuskaitstuks.',
    errScopeNotGranted: '{app}-il pole enam seda juurdepääsu. Loend näitab praegust seisu.',
    errNotAllowable: 'Seda toimingut ei saa küsimata lubada.',
    errReadOnly: 'See ühendus on seatud valikule Lugemine, seega selles ei saa midagi lubada.',
    errNotSaved: 'Seda ei salvestatud. Miski ei muutunud.',
    errNotConfirmed: 'Muudatust ei õnnestunud kinnitada. Loend näitab praegust seisu.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Salvesta esitlus oma Raamatukokku',
    action_save_document: 'Salvesta dokument oma Raamatukokku',
    action_save_table: 'Salvesta tabel oma Raamatukokku',
    action_save_chart: 'Salvesta diagramm oma Raamatukokku',
    action_save_page: 'Salvesta leht oma Raamatukokku',
    action_add_to_poneva_files: 'Lisa fail oma Poneva failide hulka',
    action_save_poneva_skill_draft: 'Salvesta Skilli kavand',
    action_import_agent_skill: 'Impordi Skill',
    action_evaluate_poneva_skill: 'Hinda Skilli',
    action_ask_poneva: 'Küsi Ponevalt privaatvestluses',
    action_continue_in_poneva: 'Jätka Poneva vestluses',
    action_design_shopping_assistant: 'Salvesta ostuassistendi kujundus',
    action_storefront_preview_turn: 'Eelvaata ostuassistendi vastust',
    action_test_shopping_assistant: 'Testi ostuassistenti küsimusega',
    action_fix_shopping_assistant: 'Paranda ostuassistent',
    action_check_shopping_assistant_again: 'Kontrolli ostuassistenti uuesti',
    action_build_poneva_app: 'Ehita Live App',
    action_delete_poneva_app: 'Kustuta Live App',
    action_update_item_sharing: 'Muuda, kellega üksust jagatakse',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Kasutab krediite.',
    note_storefront_preview_turn: 'Kasutab krediite.',
    note_test_shopping_assistant: 'Kasutab krediite.',
    note_fix_shopping_assistant: 'Kasutab krediite.',
    note_check_shopping_assistant_again: 'Kasutab krediite.',
    note_update_item_sharing: 'Nad näevad seda kohe. Saad jagamise lõpetada.',
    note_delete_poneva_app: 'Ei saa tagasi võtta.',
  },
  fi: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Yhteyksiesi käyttö',
    connDescribe:
      'Slack, Pipedream-sovellukset, oma MCP ja Postgres. Muutokset vain yhteydellä, joka on asetettu tilaan “Lukeminen ja kirjoittaminen”.',
    connConfirmOn:
      'Sallitaanko, että {app} käyttää yrityksen {company} yhteyksiä sinuna? Kaikki, mikä muuttaa jotakin, odottaa sinua Saapuneet-kansiossa, ellet salli sitä.',
    connConfirmOff: 'Estetäänkö {app}:ta käyttämästä yhteyksiäsi?',
    connEnabled: '{app} voi nyt käyttää yhteyksiäsi.',
    connDisabled: '{app} ei voi enää käyttää yhteyksiäsi.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Lue kauppoja reaaliajassa',
    storesDescribe:
      'Ajantasaiset hinnat, saatavuus ja tuotetiedot oikeista verkkokaupoista Ponevan sertifioiduilla kauppatyökaluilla. Jokainen haku käyttää krediittejä.',
    storesConfirmOn:
      'Sallitaanko, että {app} lukee kauppoja reaaliajassa puolestasi? Jokainen haku käyttää yrityksen {company} krediittejä.',
    storesConfirmOff: 'Estetäänkö, ettei {app} enää lue kauppoja reaaliajassa?',
    storesEnabled: '{app} voi nyt lukea kauppoja reaaliajassa.',
    storesDisabled: '{app} ei voi enää lukea kauppoja reaaliajassa.',
    mediaTitle: 'Mediaseuranta',
    mediaDescribe:
      'Hae ja lainaa, mitä sanottiin podcasteissa ja radiossa, joita Ponevan media-arkisto kerää.',
    mediaConfirmOn:
      'Sallitaanko, että {app} hakee yrityksen {company} mediaseurannasta puolestasi?',
    mediaConfirmOff: 'Estetäänkö, ettei {app} enää hae mediaseurannasta?',
    mediaEnabled: '{app} voi nyt hakea mediaseurannasta.',
    mediaDisabled: '{app} ei voi enää hakea mediaseurannasta.',
    // The two modes of a permission family.
    modeAsk: 'Kysy joka kerta',
    modeAutomatic: 'Automaattinen',
    modeGroupLabel: '{family}: hyväksyntä',
    askExplain: 'Jokainen toiminto odottaa sinua Saapuneet-kansiossa, ellet salli sitä alla.',
    automaticSave:
      '{app} tallentaa töitä ja kysyy Ponevalta kysymättä sinulta, myös sellaista, mitä ei voi perua.',
    automaticConnections:
      '{app} käyttää yhteyksiäsi kysymättä sinulta, myös muutoksiin yhteyksillä, jotka on asetettu tilaan “Lukeminen ja kirjoittaminen”. Gmail- ja Outlook-lähetykset kysyvät edelleen sinulta.',
    confirmAutomatic: 'Sallitaanko, että {app} tekee tämän kysymättä ensin?',
    modeAskSet: '{app} kysyy nyt sinulta joka kerta.',
    modeAutomaticSet: '{app} tekee tämän nyt kysymättä.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Sallittu kysymättä',
    allowedOf: '{allowed} / {total}',
    allowedNone: 'Ei vielä yhtään',
    allowedActions: 'one:toiminto|other:toimintoa',
    groupReadOnly: 'Asetettu tilaan “Lukeminen”, joten mikään ei muutu',
    groupSendingAsks: 'Gmail- ja Outlook-lähetykset kysyvät edelleen sinulta',
    groupOff: 'Pois päältä',
    groupNothingToChange: 'Ei mitään muutettavaa',
    groupUnavailable: 'Lataus epäonnistui',
    showAll: 'Näytä kaikki {count} toimintoa: {name}',
    showFewer: 'Näytä vähemmän',
    loading: 'Ladataan toimintoja…',
    loadFailed: 'Näitä toimintoja ei voitu ladata.',
    retry: 'Yritä uudelleen',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Voidaan perua.',
    lockedNote: 'Kysyy aina sinulta.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Jotkin asiat kysyvät aina sinulta valitsit mitä tahansa: yrityksen omistuksen siirto, jäsenen poistaminen, Sovelluksen julkaiseminen, rahan käyttö krediittien lisäksi, {app}:n kirjoittaman Sovelluksen koodin vahvistaminen sekä Gmail- tai Outlook-lähetykset. Toiminnon salliminen ei koskaan anna {app}:lle enempää kuin sinulla on, ja tilaan “Lukeminen” asetettu yhteys pysyy vain luettavana.',
    errScopeNotGranted: '{app}:lla ei ole enää tätä käyttöoikeutta. Luettelo näyttää nykytilan.',
    errNotAllowable: 'Tätä toimintoa ei voi sallia kysymättä.',
    errReadOnly: 'Tämä yhteys on asetettu tilaan “Lukeminen”, joten sillä ei voi sallia mitään.',
    errNotSaved: 'Tätä ei tallennettu. Mikään ei muuttunut.',
    errNotConfirmed: 'Muutosta ei voitu vahvistaa. Luettelo näyttää nykytilan.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Tallenna esitys kirjastoosi',
    action_save_document: 'Tallenna asiakirja kirjastoosi',
    action_save_table: 'Tallenna taulukko kirjastoosi',
    action_save_chart: 'Tallenna kaavio kirjastoosi',
    action_save_page: 'Tallenna sivu kirjastoosi',
    action_add_to_poneva_files: 'Lisää tiedosto Poneva-tiedostoihisi',
    action_save_poneva_skill_draft: 'Tallenna Skillin luonnos',
    action_import_agent_skill: 'Tuo Skill',
    action_evaluate_poneva_skill: 'Arvioi Skill',
    action_ask_poneva: 'Kysy Ponevalta yksityisessä keskustelussa',
    action_continue_in_poneva: 'Jatka Poneva-keskustelussa',
    action_design_shopping_assistant: 'Tallenna ostosavustajan suunnittelu',
    action_storefront_preview_turn: 'Esikatsele ostosavustajan vastausta',
    action_test_shopping_assistant: 'Testaa ostosavustajaa kysymyksellä',
    action_fix_shopping_assistant: 'Korjaa ostosavustaja',
    action_check_shopping_assistant_again: 'Tarkista ostosavustaja uudelleen',
    action_build_poneva_app: 'Rakenna Live App',
    action_delete_poneva_app: 'Poista Live App',
    action_update_item_sharing: 'Muuta, kenen kanssa kohde jaetaan',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Käyttää krediittejä.',
    note_storefront_preview_turn: 'Käyttää krediittejä.',
    note_test_shopping_assistant: 'Käyttää krediittejä.',
    note_fix_shopping_assistant: 'Käyttää krediittejä.',
    note_check_shopping_assistant_again: 'Käyttää krediittejä.',
    note_update_item_sharing: 'He näkevät sen heti. Voit lopettaa jakamisen.',
    note_delete_poneva_app: 'Ei voi perua.',
  },
  fr: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Utiliser vos connexions',
    connDescribe:
      'Slack, applications Pipedream, MCP personnalisé et Postgres. Les modifications ne passent que sur une connexion réglée sur Lecture et écriture.',
    connConfirmOn:
      'Laisser {app} utiliser les connexions de {company} comme vous ? Tout ce qui modifie quelque chose vous attend dans votre boîte de réception, sauf si vous l’autorisez.',
    connConfirmOff: 'Empêcher {app} d’utiliser vos connexions ?',
    connEnabled: '{app} peut désormais utiliser vos connexions.',
    connDisabled: '{app} ne peut plus utiliser vos connexions.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Lire les boutiques en direct',
    storesDescribe:
      'Prix, stocks et détails produits actuels de vraies boutiques en ligne, avec les outils certifiés de Poneva. Chaque lecture utilise des crédits.',
    storesConfirmOn:
      'Autoriser {app} à lire les boutiques en direct en votre nom ? Chaque lecture utilise les crédits de {company}.',
    storesConfirmOff: 'Empêcher {app} de lire les boutiques en direct ?',
    storesEnabled: '{app} peut désormais lire les boutiques en direct.',
    storesDisabled: '{app} ne peut plus lire les boutiques en direct.',
    mediaTitle: 'Veille médias',
    mediaDescribe:
      'Recherchez et citez ce qui a été dit dans les podcasts et à la radio que l’archive médias de Poneva collecte.',
    mediaConfirmOn:
      'Autoriser {app} à rechercher dans la veille médias de {company} en votre nom ?',
    mediaConfirmOff: 'Empêcher {app} de rechercher dans la veille médias ?',
    mediaEnabled: '{app} peut désormais rechercher dans la veille médias.',
    mediaDisabled: '{app} ne peut plus rechercher dans la veille médias.',
    // The two modes of a permission family.
    modeAsk: 'Demander à chaque fois',
    modeAutomatic: 'Automatique',
    modeGroupLabel: '{family} : validation',
    askExplain:
      'Chaque action vous attend dans votre boîte de réception, sauf si vous l’autorisez ci-dessous.',
    automaticSave:
      '{app} enregistre du travail et interroge Poneva sans vous demander, y compris des choses qui ne peuvent pas être annulées.',
    automaticConnections:
      '{app} utilise vos connexions sans vous demander, y compris pour modifier des connexions réglées sur Lecture et écriture. Les envois depuis Gmail et Outlook vous sont toujours demandés.',
    confirmAutomatic: 'Laisser {app} le faire sans vous demander d’abord ?',
    modeAskSet: '{app} vous demande désormais à chaque fois.',
    modeAutomaticSet: '{app} le fait désormais sans demander.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Autorisé sans demander',
    allowedOf: '{allowed} sur {total}',
    allowedNone: 'Aucune pour l’instant',
    allowedActions: 'one:action|other:actions',
    groupReadOnly: 'Réglée sur Lecture, donc rien ne change',
    groupSendingAsks: 'Les envois depuis Gmail et Outlook vous sont toujours demandés',
    groupOff: 'Désactivée',
    groupNothingToChange: 'Rien à modifier',
    groupUnavailable: 'Chargement impossible',
    showAll: 'Afficher les {count} actions de {name}',
    showFewer: 'Afficher moins',
    loading: 'Chargement des actions…',
    loadFailed: 'Impossible de charger ces actions.',
    retry: 'Réessayer',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Peut être annulé.',
    lockedNote: 'Vous est toujours demandé.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Certaines choses vous sont toujours demandées, quel que soit votre choix : transférer la propriété de l’entreprise, retirer un membre, rendre une App publique, dépenser de l’argent au-delà des crédits, confirmer le code d’App écrit par {app}, et les envois Gmail ou Outlook. Autoriser une action ne donne jamais à {app} plus que ce que vous avez, et une connexion réglée sur Lecture reste en lecture seule.',
    errScopeNotGranted: '{app} n’a plus cet accès. La liste montre l’état actuel.',
    errNotAllowable: 'Cette action ne peut pas être autorisée sans demander.',
    errReadOnly: 'Cette connexion est réglée sur Lecture, donc rien ne peut y être autorisé.',
    errNotSaved: 'Cela n’a pas été enregistré. Rien n’a changé.',
    errNotConfirmed: 'Impossible de confirmer ce changement. La liste montre l’état actuel.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Enregistrer une présentation dans votre Bibliothèque',
    action_save_document: 'Enregistrer un document dans votre Bibliothèque',
    action_save_table: 'Enregistrer un tableau dans votre Bibliothèque',
    action_save_chart: 'Enregistrer un graphique dans votre Bibliothèque',
    action_save_page: 'Enregistrer une page dans votre Bibliothèque',
    action_add_to_poneva_files: 'Ajouter un fichier à vos fichiers Poneva',
    action_save_poneva_skill_draft: 'Enregistrer le brouillon d’un Skill',
    action_import_agent_skill: 'Importer un Skill',
    action_evaluate_poneva_skill: 'Évaluer un Skill',
    action_ask_poneva: 'Interroger Poneva dans une discussion privée',
    action_continue_in_poneva: 'Continuer dans une discussion Poneva',
    action_design_shopping_assistant: 'Enregistrer la conception d’un assistant d’achat',
    action_storefront_preview_turn: 'Prévisualiser la réponse d’un assistant d’achat',
    action_test_shopping_assistant: 'Tester un assistant d’achat avec une question',
    action_fix_shopping_assistant: 'Réparer un assistant d’achat',
    action_check_shopping_assistant_again: 'Revérifier un assistant d’achat',
    action_build_poneva_app: 'Créer une Live App',
    action_delete_poneva_app: 'Supprimer une Live App',
    action_update_item_sharing: 'Modifier avec qui un élément est partagé',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Utilise des crédits.',
    note_storefront_preview_turn: 'Utilise des crédits.',
    note_test_shopping_assistant: 'Utilise des crédits.',
    note_fix_shopping_assistant: 'Utilise des crédits.',
    note_check_shopping_assistant_again: 'Utilise des crédits.',
    note_update_item_sharing: 'Elles le voient tout de suite. Vous pouvez arrêter le partage.',
    note_delete_poneva_app: 'Ne peut pas être annulé.',
  },
  ga: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Do naisc a úsáid',
    connDescribe:
      'Slack, aipeanna Pipedream, do MCP féin agus Postgres. Athruithe ar nasc atá socraithe go Léamh agus scríobh amháin.',
    connConfirmOn:
      'An ligfidh tú do {app} naisc {company} a úsáid mar tusa? Fanann gach rud a athraíonn rud éigin leat sa Bhosca isteach, mura gceadaíonn tú é.',
    connConfirmOff: 'An stopfaidh tú {app} ó do naisc a úsáid?',
    connEnabled: 'Is féidir le {app} do naisc a úsáid anois.',
    connDisabled: 'Ní féidir le {app} do naisc a úsáid a thuilleadh.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Siopaí a léamh beo',
    storesDescribe:
      'Praghsanna, stoc agus sonraí táirge reatha ó shiopaí gréasáin fíora, le huirlisí siopa deimhnithe Poneva. Úsáideann gach léamh creidmheasanna.',
    storesConfirmOn:
      'Ligean do {app} siopaí a léamh beo mar tú féin? Úsáideann gach léamh creidmheasanna {company}.',
    storesConfirmOff: 'Stop a chur le {app} ó shiopaí a léamh beo?',
    storesEnabled: 'Is féidir le {app} siopaí a léamh beo anois.',
    storesDisabled: 'Ní féidir le {app} siopaí a léamh beo a thuilleadh.',
    mediaTitle: 'Monatóireacht meán',
    mediaDescribe:
      'Cuardaigh agus luaigh a ndúradh ar na podchraoltaí agus ar an raidió a bhailíonn cartlann meán Poneva.',
    mediaConfirmOn: 'Ligean do {app} monatóireacht meán {company} a chuardach mar tú féin?',
    mediaConfirmOff: 'Stop a chur le {app} ó mhonatóireacht meán a chuardach?',
    mediaEnabled: 'Is féidir le {app} monatóireacht meán a chuardach anois.',
    mediaDisabled: 'Ní féidir le {app} monatóireacht meán a chuardach a thuilleadh.',
    // The two modes of a permission family.
    modeAsk: 'Fiafraigh gach uair',
    modeAutomatic: 'Uathoibríoch',
    modeGroupLabel: '{family}: formheas',
    askExplain: 'Fanann gach gníomh leat sa Bhosca isteach, mura gceadaíonn tú é thíos.',
    automaticSave:
      'Sábhálann {app} obair agus fiafraíonn sé de Poneva gan fiafraí díot, lena n-áirítear rudaí nach féidir a chealú.',
    automaticConnections:
      'Úsáideann {app} do naisc gan fiafraí díot, lena n-áirítear athruithe ar naisc atá socraithe go Léamh agus scríobh. Fiafraíonn seoltaí Gmail agus Outlook díot fós.',
    confirmAutomatic: 'An ligfidh tú do {app} é seo a dhéanamh gan fiafraí díot ar dtús?',
    modeAskSet: 'Fiafraíonn {app} díot gach uair anois.',
    modeAutomaticSet: 'Déanann {app} é seo gan fiafraí anois.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Ceadaithe gan fiafraí',
    allowedOf: '{allowed} as {total}',
    allowedNone: 'Aon cheann fós',
    allowedActions: 'one:gníomh|two:gníomh|few:gníomhartha|many:gníomhartha|other:gníomhartha',
    groupReadOnly: 'Socraithe go Léamh, mar sin ní athraíonn aon rud',
    groupSendingAsks: 'Fiafraíonn seoltaí Gmail agus Outlook díot fós',
    groupOff: 'Múchta',
    groupNothingToChange: 'Aon rud le hathrú',
    groupUnavailable: 'Níorbh fhéidir a lódáil',
    showAll: 'Taispeáin na {count} gníomh ar fad de chuid {name}',
    showFewer: 'Taispeáin níos lú',
    loading: 'Gníomhartha á lódáil…',
    loadFailed: 'Níorbh fhéidir na gníomhartha seo a lódáil.',
    retry: 'Bain triail eile as',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Is féidir é a chealú.',
    lockedNote: 'Fiafraíonn sé díot i gcónaí.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Fiafraíonn roinnt rudaí díot i gcónaí, pé rud a roghnaíonn tú: úinéireacht na cuideachta a aistriú, ball a bhaint, Aip a dhéanamh poiblí, airgead a chaitheamh thar chreidmheasanna, cód Aipe a scríobh {app} a dheimhniú, agus seoltaí Gmail nó Outlook. Ní thugann gníomh a cheadú níos mó do {app} ná mar atá agat féin, agus fanann nasc atá socraithe go Léamh inléite amháin.',
    errScopeNotGranted:
      'Níl an rochtain seo ag {app} a thuilleadh. Taispeánann an liosta an staid reatha.',
    errNotAllowable: 'Ní féidir an gníomh sin a cheadú gan fiafraí.',
    errReadOnly: 'Tá an nasc sin socraithe go Léamh, mar sin ní féidir aon rud air a cheadú.',
    errNotSaved: 'Níor sábháladh é sin. Níor athraigh aon rud.',
    errNotConfirmed:
      'Níorbh fhéidir an t-athrú sin a dheimhniú. Taispeánann an liosta an staid reatha.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Sábháil cur i láthair i do Leabharlann',
    action_save_document: 'Sábháil doiciméad i do Leabharlann',
    action_save_table: 'Sábháil tábla i do Leabharlann',
    action_save_chart: 'Sábháil cairt i do Leabharlann',
    action_save_page: 'Sábháil leathanach i do Leabharlann',
    action_add_to_poneva_files: 'Cuir comhad le do chomhaid Poneva',
    action_save_poneva_skill_draft: 'Sábháil dréacht de Skill',
    action_import_agent_skill: 'Iompórtáil Skill',
    action_evaluate_poneva_skill: 'Meas Skill',
    action_ask_poneva: 'Fiafraigh de Poneva i gcomhrá príobháideach',
    action_continue_in_poneva: 'Lean ar aghaidh i gcomhrá Poneva',
    action_design_shopping_assistant: 'Sábháil dearadh cúntóra siopadóireachta',
    action_storefront_preview_turn: 'Réamhamharc ar fhreagra cúntóra siopadóireachta',
    action_test_shopping_assistant: 'Tástáil cúntóir siopadóireachta le ceist',
    action_fix_shopping_assistant: 'Deisigh cúntóir siopadóireachta',
    action_check_shopping_assistant_again: 'Seiceáil cúntóir siopadóireachta arís',
    action_build_poneva_app: 'Tóg Live App',
    action_delete_poneva_app: 'Scrios Live App',
    action_update_item_sharing: 'Athraigh cé leis a roinntear mír',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Úsáideann sé creidmheasanna.',
    note_storefront_preview_turn: 'Úsáideann sé creidmheasanna.',
    note_test_shopping_assistant: 'Úsáideann sé creidmheasanna.',
    note_fix_shopping_assistant: 'Úsáideann sé creidmheasanna.',
    note_check_shopping_assistant_again: 'Úsáideann sé creidmheasanna.',
    note_update_item_sharing:
      'Feiceann siad láithreach é. Is féidir leat stop a chur leis an roinnt.',
    note_delete_poneva_app: 'Ní féidir é a chealú.',
  },
  hr: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Korištenje vaših veza',
    connDescribe:
      'Slack, aplikacije Pipedream, vlastiti MCP i Postgres. Promjene samo na vezi postavljenoj na Čitanje i pisanje.',
    connConfirmOn:
      'Dopustiti da {app} koristi veze tvrtke {company} kao vi? Sve što nešto mijenja čeka vas u Pristigloj pošti, osim ako to dopustite.',
    connConfirmOff: 'Spriječiti {app} da koristi vaše veze?',
    connEnabled: '{app} sada može koristiti vaše veze.',
    connDisabled: '{app} više ne može koristiti vaše veze.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Čitanje trgovina uživo',
    storesDescribe:
      'Trenutačne cijene, zalihe i pojedinosti o proizvodima iz stvarnih web-trgovina, s Ponevinim certificiranim alatima za trgovine. Svako čitanje troši kredite.',
    storesConfirmOn:
      'Dopustiti aplikaciji {app} da čita trgovine uživo u vaše ime? Svako čitanje troši kredite tvrtke {company}.',
    storesConfirmOff: 'Zabraniti aplikaciji {app} čitanje trgovina uživo?',
    storesEnabled: '{app} sada može čitati trgovine uživo.',
    storesDisabled: '{app} više ne može čitati trgovine uživo.',
    mediaTitle: 'Praćenje medija',
    mediaDescribe:
      'Pretražujte i citirajte što je rečeno u podcastima i na radiju koje prikuplja Ponevin medijski arhiv.',
    mediaConfirmOn:
      'Dopustiti aplikaciji {app} da pretražuje praćenje medija tvrtke {company} u vaše ime?',
    mediaConfirmOff: 'Zabraniti aplikaciji {app} pretraživanje praćenja medija?',
    mediaEnabled: '{app} sada može pretraživati praćenje medija.',
    mediaDisabled: '{app} više ne može pretraživati praćenje medija.',
    // The two modes of a permission family.
    modeAsk: 'Pitaj svaki put',
    modeAutomatic: 'Automatski',
    modeGroupLabel: '{family}: odobrenje',
    askExplain: 'Svaka radnja čeka vas u Pristigloj pošti, osim ako je dopustite u nastavku.',
    automaticSave:
      '{app} sprema rad i pita Ponevu bez pitanja vas, uključujući stvari koje se ne mogu poništiti.',
    automaticConnections:
      '{app} koristi vaše veze bez pitanja vas, uključujući promjene na vezama postavljenima na Čitanje i pisanje. Slanja iz Gmaila i Outlooka i dalje pitaju vas.',
    confirmAutomatic: 'Dopustiti da {app} to učini bez prethodnog pitanja?',
    modeAskSet: '{app} vas sada pita svaki put.',
    modeAutomaticSet: '{app} sada to radi bez pitanja.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Dopušteno bez pitanja',
    allowedOf: '{allowed} od {total}',
    allowedNone: 'Još nijedna',
    allowedActions: 'one:radnja|few:radnje|other:radnji',
    groupReadOnly: 'Postavljeno na Čitanje, pa se ništa ne mijenja',
    groupSendingAsks: 'Slanja iz Gmaila i Outlooka i dalje pitaju vas',
    groupOff: 'Isključeno',
    groupNothingToChange: 'Nema što mijenjati',
    groupUnavailable: 'Učitavanje nije uspjelo',
    showAll: 'Prikaži svih {count} radnji u {name}',
    showFewer: 'Prikaži manje',
    loading: 'Učitavanje radnji…',
    loadFailed: 'Te radnje nije bilo moguće učitati.',
    retry: 'Pokušaj ponovno',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Može se poništiti.',
    lockedNote: 'Uvijek pita vas.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Neke stvari uvijek pitaju vas, što god odabrali: prijenos vlasništva tvrtke, uklanjanje člana, objavljivanje Aplikacije, trošenje novca izvan kredita, potvrđivanje koda Aplikacije koji je napisao {app} te slanje iz Gmaila ili Outlooka. Dopuštanje radnje nikad ne daje {app} više nego što imate vi, a veza postavljena na Čitanje ostaje samo za čitanje.',
    errScopeNotGranted: '{app} više nema taj pristup. Popis prikazuje trenutačno stanje.',
    errNotAllowable: 'Tu radnju nije moguće dopustiti bez pitanja.',
    errReadOnly: 'Ta je veza postavljena na Čitanje, pa se na njoj ništa ne može dopustiti.',
    errNotSaved: 'Nije spremljeno. Ništa se nije promijenilo.',
    errNotConfirmed: 'Promjenu nije bilo moguće potvrditi. Popis prikazuje trenutačno stanje.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Spremi prezentaciju u Biblioteku',
    action_save_document: 'Spremi dokument u Biblioteku',
    action_save_table: 'Spremi tablicu u Biblioteku',
    action_save_chart: 'Spremi grafikon u Biblioteku',
    action_save_page: 'Spremi stranicu u Biblioteku',
    action_add_to_poneva_files: 'Dodaj datoteku među Poneva datoteke',
    action_save_poneva_skill_draft: 'Spremi skicu Skilla',
    action_import_agent_skill: 'Uvezi Skill',
    action_evaluate_poneva_skill: 'Procijeni Skill',
    action_ask_poneva: 'Pitaj Ponevu u privatnom razgovoru',
    action_continue_in_poneva: 'Nastavi u Poneva razgovoru',
    action_design_shopping_assistant: 'Spremi dizajn asistenta za kupnju',
    action_storefront_preview_turn: 'Pretpregled odgovora asistenta za kupnju',
    action_test_shopping_assistant: 'Testiraj asistenta za kupnju pitanjem',
    action_fix_shopping_assistant: 'Popravi asistenta za kupnju',
    action_check_shopping_assistant_again: 'Ponovno provjeri asistenta za kupnju',
    action_build_poneva_app: 'Izgradi Live App',
    action_delete_poneva_app: 'Izbriši Live App',
    action_update_item_sharing: 'Promijeni s kim se stavka dijeli',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Troši kredite.',
    note_storefront_preview_turn: 'Troši kredite.',
    note_test_shopping_assistant: 'Troši kredite.',
    note_fix_shopping_assistant: 'Troši kredite.',
    note_check_shopping_assistant_again: 'Troši kredite.',
    note_update_item_sharing: 'Vide je odmah. Dijeljenje možete zaustaviti.',
    note_delete_poneva_app: 'Ne može se poništiti.',
  },
  hu: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Az Ön kapcsolatainak használata',
    connDescribe:
      'Slack, Pipedream-alkalmazások, saját MCP és Postgres. Módosítás csak az Olvasás és írás beállítású kapcsolaton történik.',
    connConfirmOn:
      'Engedi, hogy a(z) {app} a(z) {company} kapcsolatait az Ön nevében használja? Minden, ami módosít valamit, a Beérkezett üzenetek között vár Önre, hacsak nem engedélyezi.',
    connConfirmOff: 'Megakadályozza, hogy a(z) {app} használja az Ön kapcsolatait?',
    connEnabled: 'A(z) {app} mostantól használhatja az Ön kapcsolatait.',
    connDisabled: 'A(z) {app} többé nem használhatja az Ön kapcsolatait.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Üzletek élő olvasása',
    storesDescribe:
      'Aktuális árak, készlet és termékadatok valódi webáruházakból, a Poneva tanúsított üzleti eszközeivel. Minden olvasás kreditet használ.',
    storesConfirmOn:
      'Engedélyezi, hogy a(z) {app} az Ön nevében élőben olvassa az üzleteket? Minden olvasás a(z) {company} kreditjét használja.',
    storesConfirmOff: 'Letiltja, hogy a(z) {app} élőben olvassa az üzleteket?',
    storesEnabled: 'A(z) {app} mostantól élőben olvashatja az üzleteket.',
    storesDisabled: 'A(z) {app} már nem olvashatja élőben az üzleteket.',
    mediaTitle: 'Médiafigyelés',
    mediaDescribe:
      'Keresés és idézés abból, ami a Poneva médiaarchívuma által gyűjtött podcastokban és rádióműsorokban elhangzott.',
    mediaConfirmOn:
      'Engedélyezi, hogy a(z) {app} az Ön nevében keressen a(z) {company} médiafigyelésében?',
    mediaConfirmOff: 'Letiltja, hogy a(z) {app} a médiafigyelésben keressen?',
    mediaEnabled: 'A(z) {app} mostantól kereshet a médiafigyelésben.',
    mediaDisabled: 'A(z) {app} már nem kereshet a médiafigyelésben.',
    // The two modes of a permission family.
    modeAsk: 'Kérdezzen minden alkalommal',
    modeAutomatic: 'Automatikus',
    modeGroupLabel: '{family}: jóváhagyás',
    askExplain:
      'Minden művelet a Beérkezett üzenetek között vár Önre, hacsak lent nem engedélyezi.',
    automaticSave:
      'A(z) {app} munkát ment és kérdez a Ponevától anélkül, hogy Önt megkérdezné, olyan dolgokat is, amelyek nem vonhatók vissza.',
    automaticConnections:
      'A(z) {app} az Ön megkérdezése nélkül használja az Ön kapcsolatait, az Olvasás és írás beállítású kapcsolatokon végzett módosításokat is beleértve. A Gmail- és Outlook-küldések továbbra is Önt kérdezik.',
    confirmAutomatic: 'Engedi, hogy a(z) {app} ezt előzetes kérdés nélkül megtegye?',
    modeAskSet: 'A(z) {app} mostantól minden alkalommal megkérdezi Önt.',
    modeAutomaticSet: 'A(z) {app} mostantól ezt kérdezés nélkül teszi.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Kérdezés nélkül engedélyezett',
    allowedOf: '{allowed} / {total}',
    allowedNone: 'Még egy sem',
    allowedActions: 'one:művelet|other:művelet',
    groupReadOnly: 'Olvasás módra állítva, így semmi sem változik',
    groupSendingAsks: 'A Gmail- és Outlook-küldések továbbra is Önt kérdezik',
    groupOff: 'Kikapcsolva',
    groupNothingToChange: 'Nincs mit módosítania',
    groupUnavailable: 'Nem sikerült betölteni',
    showAll: 'Mind a(z) {count} {name}-művelet megjelenítése',
    showFewer: 'Kevesebb megjelenítése',
    loading: 'Műveletek betöltése…',
    loadFailed: 'Ezeket a műveleteket nem sikerült betölteni.',
    retry: 'Próbálja újra',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Visszavonható.',
    lockedNote: 'Mindig Önt kérdezi.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Egyes dolgok mindig Önt kérdezik, bármit is választ: a cég tulajdonjogának átruházása, tag eltávolítása, Alkalmazás nyilvánossá tétele, a kreditek feletti pénzköltés, a(z) {app} által írt Alkalmazáskód megerősítése, valamint a Gmail- vagy Outlook-küldések. Egy művelet engedélyezése sosem ad a(z) {app} számára többet annál, amije Önnek van, és az Olvasás módra állított kapcsolat csak olvasható marad.',
    errScopeNotGranted:
      'A(z) {app} már nem rendelkezik ezzel a hozzáféréssel. A lista a jelenlegi állapotot mutatja.',
    errNotAllowable: 'Ezt a műveletet nem lehet kérdezés nélkül engedélyezni.',
    errReadOnly: 'Ez a kapcsolat Olvasás módra van állítva, így rajta semmi sem engedélyezhető.',
    errNotSaved: 'Ez nem lett mentve. Semmi sem változott.',
    errNotConfirmed:
      'A módosítást nem sikerült megerősíteni. A lista a jelenlegi állapotot mutatja.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Előadás mentése a Könyvtárba',
    action_save_document: 'Dokumentum mentése a Könyvtárba',
    action_save_table: 'Táblázat mentése a Könyvtárba',
    action_save_chart: 'Diagram mentése a Könyvtárba',
    action_save_page: 'Oldal mentése a Könyvtárba',
    action_add_to_poneva_files: 'Fájl hozzáadása a Poneva-fájlokhoz',
    action_save_poneva_skill_draft: 'Skill-vázlat mentése',
    action_import_agent_skill: 'Skill importálása',
    action_evaluate_poneva_skill: 'Skill értékelése',
    action_ask_poneva: 'Kérdezés a Ponevától privát csevegésben',
    action_continue_in_poneva: 'Folytatás Poneva-csevegésben',
    action_design_shopping_assistant: 'Vásárlási asszisztens tervének mentése',
    action_storefront_preview_turn: 'Vásárlási asszisztens válaszának előnézete',
    action_test_shopping_assistant: 'Vásárlási asszisztens tesztelése egy kérdéssel',
    action_fix_shopping_assistant: 'Vásárlási asszisztens javítása',
    action_check_shopping_assistant_again: 'Vásárlási asszisztens ismételt ellenőrzése',
    action_build_poneva_app: 'Live App készítése',
    action_delete_poneva_app: 'Live App törlése',
    action_update_item_sharing: 'Annak módosítása, kivel van megosztva egy elem',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Krediteket használ.',
    note_storefront_preview_turn: 'Krediteket használ.',
    note_test_shopping_assistant: 'Krediteket használ.',
    note_fix_shopping_assistant: 'Krediteket használ.',
    note_check_shopping_assistant_again: 'Krediteket használ.',
    note_update_item_sharing: 'Azonnal látják. A megosztást leállíthatja.',
    note_delete_poneva_app: 'Nem vonható vissza.',
  },
  it: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Usare le tue connessioni',
    connDescribe:
      'Slack, app Pipedream, MCP personalizzato e Postgres. Le modifiche avvengono solo su una connessione impostata su Lettura e scrittura.',
    connConfirmOn:
      'Permettere a {app} di usare le connessioni di {company} come te? Tutto ciò che modifica qualcosa ti aspetta nella Posta in arrivo, a meno che tu non lo consenta.',
    connConfirmOff: 'Impedire a {app} di usare le tue connessioni?',
    connEnabled: '{app} ora può usare le tue connessioni.',
    connDisabled: '{app} non può più usare le tue connessioni.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Leggere i negozi dal vivo',
    storesDescribe:
      'Prezzi, disponibilità e dettagli dei prodotti aggiornati da veri negozi online, con gli strumenti certificati di Poneva. Ogni lettura usa crediti.',
    storesConfirmOn:
      'Consentire a {app} di leggere i negozi dal vivo a tuo nome? Ogni lettura usa i crediti di {company}.',
    storesConfirmOff: 'Impedire a {app} di leggere i negozi dal vivo?',
    storesEnabled: '{app} ora può leggere i negozi dal vivo.',
    storesDisabled: '{app} non può più leggere i negozi dal vivo.',
    mediaTitle: 'Monitoraggio dei media',
    mediaDescribe:
      'Cerca e cita ciò che è stato detto nei podcast e alla radio raccolti dall’archivio media di Poneva.',
    mediaConfirmOn:
      'Consentire a {app} di cercare nel monitoraggio dei media di {company} a tuo nome?',
    mediaConfirmOff: 'Impedire a {app} di cercare nel monitoraggio dei media?',
    mediaEnabled: '{app} ora può cercare nel monitoraggio dei media.',
    mediaDisabled: '{app} non può più cercare nel monitoraggio dei media.',
    // The two modes of a permission family.
    modeAsk: 'Chiedi ogni volta',
    modeAutomatic: 'Automatico',
    modeGroupLabel: '{family}: approvazione',
    askExplain:
      'Ogni azione ti aspetta nella Posta in arrivo, a meno che tu non la consenta qui sotto.',
    automaticSave:
      '{app} salva il lavoro e interroga Poneva senza chiederti nulla, anche per cose che non si possono annullare.',
    automaticConnections:
      '{app} usa le tue connessioni senza chiederti nulla, anche per modificare connessioni impostate su Lettura e scrittura. Gli invii da Gmail e Outlook te lo chiedono ancora.',
    confirmAutomatic: 'Permettere a {app} di farlo senza chiederti prima?',
    modeAskSet: '{app} ora te lo chiede ogni volta.',
    modeAutomaticSet: '{app} ora lo fa senza chiedere.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Consentito senza chiedere',
    allowedOf: '{allowed} su {total}',
    allowedNone: 'Ancora nessuna',
    allowedActions: 'one:azione|other:azioni',
    groupReadOnly: 'Impostata su Lettura, quindi non cambia nulla',
    groupSendingAsks: 'Gli invii da Gmail e Outlook te lo chiedono ancora',
    groupOff: 'Disattivata',
    groupNothingToChange: 'Niente da modificare',
    groupUnavailable: 'Impossibile caricare',
    showAll: 'Mostra tutte le {count} azioni di {name}',
    showFewer: 'Mostra meno',
    loading: 'Caricamento delle azioni…',
    loadFailed: 'Impossibile caricare queste azioni.',
    retry: 'Riprova',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Si può annullare.',
    lockedNote: 'Te lo chiede sempre.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Alcune cose te lo chiedono sempre, qualunque cosa tu scelga: trasferire la proprietà dell’azienda, rimuovere un membro, rendere pubblica una App, spendere denaro oltre i crediti, confermare il codice di App scritto da {app} e gli invii da Gmail o Outlook. Consentire un’azione non dà mai a {app} più di quanto hai tu, e una connessione impostata su Lettura resta di sola lettura.',
    errScopeNotGranted: '{app} non ha più questo accesso. L’elenco mostra lo stato attuale.',
    errNotAllowable: 'Quell’azione non può essere consentita senza chiedere.',
    errReadOnly: 'Quella connessione è impostata su Lettura, quindi non si può consentire nulla.',
    errNotSaved: 'Non è stato salvato. Non è cambiato nulla.',
    errNotConfirmed: 'Impossibile confermare la modifica. L’elenco mostra lo stato attuale.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Salva una presentazione nella tua Libreria',
    action_save_document: 'Salva un documento nella tua Libreria',
    action_save_table: 'Salva una tabella nella tua Libreria',
    action_save_chart: 'Salva un grafico nella tua Libreria',
    action_save_page: 'Salva una pagina nella tua Libreria',
    action_add_to_poneva_files: 'Aggiungi un file ai tuoi file Poneva',
    action_save_poneva_skill_draft: 'Salva la bozza di uno Skill',
    action_import_agent_skill: 'Importa uno Skill',
    action_evaluate_poneva_skill: 'Valuta uno Skill',
    action_ask_poneva: 'Chiedi a Poneva in una chat privata',
    action_continue_in_poneva: 'Continua in una chat Poneva',
    action_design_shopping_assistant: 'Salva il design di un assistente allo shopping',
    action_storefront_preview_turn: 'Anteprima della risposta di un assistente allo shopping',
    action_test_shopping_assistant: 'Prova un assistente allo shopping con una domanda',
    action_fix_shopping_assistant: 'Correggi un assistente allo shopping',
    action_check_shopping_assistant_again: 'Ricontrolla un assistente allo shopping',
    action_build_poneva_app: 'Crea una Live App',
    action_delete_poneva_app: 'Elimina una Live App',
    action_update_item_sharing: 'Cambia con chi è condiviso un elemento',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Usa crediti.',
    note_storefront_preview_turn: 'Usa crediti.',
    note_test_shopping_assistant: 'Usa crediti.',
    note_fix_shopping_assistant: 'Usa crediti.',
    note_check_shopping_assistant_again: 'Usa crediti.',
    note_update_item_sharing: 'Lo vedono subito. Puoi interrompere la condivisione.',
    note_delete_poneva_app: 'Non si può annullare.',
  },
  lt: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Jūsų ryšių naudojimas',
    connDescribe:
      'Slack, Pipedream programėlės, nuosavas MCP ir Postgres. Pakeitimai tik ryšyje, nustatytame į Skaitymas ir rašymas.',
    connConfirmOn:
      'Leisti {app} naudoti {company} ryšius jūsų vardu? Viskas, kas ką nors keičia, laukia jūsų Gautuosiuose, nebent tai leisite.',
    connConfirmOff: 'Neleisti {app} naudoti jūsų ryšių?',
    connEnabled: '{app} dabar gali naudoti jūsų ryšius.',
    connDisabled: '{app} nebegali naudoti jūsų ryšių.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Parduotuvių skaitymas tiesiogiai',
    storesDescribe:
      'Dabartinės kainos, atsargos ir prekių informacija iš tikrų internetinių parduotuvių su sertifikuotais Poneva įrankiais. Kiekvienas skaitymas naudoja kreditus.',
    storesConfirmOn:
      'Leisti {app} jūsų vardu tiesiogiai skaityti parduotuves? Kiekvienas skaitymas naudoja {company} kreditus.',
    storesConfirmOff: 'Uždrausti {app} tiesiogiai skaityti parduotuves?',
    storesEnabled: '{app} dabar gali tiesiogiai skaityti parduotuves.',
    storesDisabled: '{app} nebegali tiesiogiai skaityti parduotuvių.',
    mediaTitle: 'Žiniasklaidos stebėsena',
    mediaDescribe:
      'Ieškokite ir cituokite, kas buvo pasakyta tinklalaidėse ir per radiją, kuriuos renka Poneva žiniasklaidos archyvas.',
    mediaConfirmOn: 'Leisti {app} jūsų vardu ieškoti {company} žiniasklaidos stebėsenoje?',
    mediaConfirmOff: 'Uždrausti {app} ieškoti žiniasklaidos stebėsenoje?',
    mediaEnabled: '{app} dabar gali ieškoti žiniasklaidos stebėsenoje.',
    mediaDisabled: '{app} nebegali ieškoti žiniasklaidos stebėsenoje.',
    // The two modes of a permission family.
    modeAsk: 'Klausti kaskart',
    modeAutomatic: 'Automatiškai',
    modeGroupLabel: '{family}: patvirtinimas',
    askExplain: 'Kiekvienas veiksmas laukia jūsų Gautuosiuose, nebent jį leisite žemiau.',
    automaticSave:
      '{app} išsaugo darbą ir klausia Poneva neklausdamas jūsų, įskaitant tai, ko neįmanoma atšaukti.',
    automaticConnections:
      '{app} naudoja jūsų ryšius neklausdamas jūsų, įskaitant pakeitimus ryšiuose, nustatytuose į Skaitymas ir rašymas. Gmail ir Outlook siuntimai vis tiek klausia jūsų.',
    confirmAutomatic: 'Leisti {app} tai daryti neklausus jūsų iš anksto?',
    modeAskSet: '{app} dabar klausia jūsų kaskart.',
    modeAutomaticSet: '{app} dabar tai daro neklausdamas.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Leidžiama neklausiant',
    allowedOf: '{allowed} iš {total}',
    allowedNone: 'Kol kas nėra',
    allowedActions: 'one:veiksmas|few:veiksmai|many:veiksmo|other:veiksmų',
    groupReadOnly: 'Nustatyta į Skaitymas, todėl niekas nesikeičia',
    groupSendingAsks: 'Gmail ir Outlook siuntimai vis tiek klausia jūsų',
    groupOff: 'Išjungta',
    groupNothingToChange: 'Nėra ką keisti',
    groupUnavailable: 'Nepavyko įkelti',
    showAll: 'Rodyti visus {count} veiksmus: {name}',
    showFewer: 'Rodyti mažiau',
    loading: 'Įkeliami veiksmai…',
    loadFailed: 'Šių veiksmų nepavyko įkelti.',
    retry: 'Bandyti dar kartą',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Galima atšaukti.',
    lockedNote: 'Visada klausia jūsų.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Kai kurie dalykai visada klausia jūsų, ką bepasirinktumėte: įmonės nuosavybės perdavimas, nario pašalinimas, Programos paskelbimas viešai, pinigų leidimas už kreditų ribų, {app} parašyto Programos kodo patvirtinimas ir Gmail ar Outlook siuntimai. Veiksmo leidimas niekada nesuteikia {app} daugiau, nei turite jūs, o ryšys, nustatytas į Skaitymas, lieka tik skaitomas.',
    errScopeNotGranted: '{app} nebeturi šios prieigos. Sąraše rodoma dabartinė būsena.',
    errNotAllowable: 'Šio veiksmo negalima leisti neklausiant.',
    errReadOnly: 'Šis ryšys nustatytas į Skaitymas, todėl jame nieko negalima leisti.',
    errNotSaved: 'Neišsaugota. Niekas nepasikeitė.',
    errNotConfirmed: 'Šio pakeitimo nepavyko patvirtinti. Sąraše rodoma dabartinė būsena.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Išsaugoti pristatymą Bibliotekoje',
    action_save_document: 'Išsaugoti dokumentą Bibliotekoje',
    action_save_table: 'Išsaugoti lentelę Bibliotekoje',
    action_save_chart: 'Išsaugoti diagramą Bibliotekoje',
    action_save_page: 'Išsaugoti puslapį Bibliotekoje',
    action_add_to_poneva_files: 'Pridėti failą prie Poneva failų',
    action_save_poneva_skill_draft: 'Išsaugoti Skill juodraštį',
    action_import_agent_skill: 'Importuoti Skill',
    action_evaluate_poneva_skill: 'Įvertinti Skill',
    action_ask_poneva: 'Paklausti Poneva privačiame pokalbyje',
    action_continue_in_poneva: 'Tęsti Poneva pokalbyje',
    action_design_shopping_assistant: 'Išsaugoti apsipirkimo asistento dizainą',
    action_storefront_preview_turn: 'Peržiūrėti apsipirkimo asistento atsakymą',
    action_test_shopping_assistant: 'Išbandyti apsipirkimo asistentą klausimu',
    action_fix_shopping_assistant: 'Pataisyti apsipirkimo asistentą',
    action_check_shopping_assistant_again: 'Dar kartą patikrinti apsipirkimo asistentą',
    action_build_poneva_app: 'Sukurti Live App',
    action_delete_poneva_app: 'Ištrinti Live App',
    action_update_item_sharing: 'Pakeisti, su kuo bendrinamas elementas',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Naudoja kreditus.',
    note_storefront_preview_turn: 'Naudoja kreditus.',
    note_test_shopping_assistant: 'Naudoja kreditus.',
    note_fix_shopping_assistant: 'Naudoja kreditus.',
    note_check_shopping_assistant_again: 'Naudoja kreditus.',
    note_update_item_sharing: 'Jie tai mato iš karto. Bendrinimą galite nutraukti.',
    note_delete_poneva_app: 'Negalima atšaukti.',
  },
  lv: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Jūsu savienojumu izmantošana',
    connDescribe:
      'Slack, Pipedream lietotnes, pašu MCP un Postgres. Izmaiņas tikai savienojumā, kas iestatīts uz Lasīšana un rakstīšana.',
    connConfirmOn:
      'Vai ļaut {app} izmantot {company} savienojumus jūsu vārdā? Viss, kas kaut ko maina, gaida jūs Iesūtnē, ja vien to neatļausiet.',
    connConfirmOff: 'Vai neļaut {app} izmantot jūsu savienojumus?',
    connEnabled: '{app} tagad var izmantot jūsu savienojumus.',
    connDisabled: '{app} vairs nevar izmantot jūsu savienojumus.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Veikalu lasīšana tiešsaistē',
    storesDescribe:
      'Aktuālās cenas, krājumi un produktu informācija no īstiem interneta veikaliem ar Poneva sertificētajiem rīkiem. Katra lasīšana izmanto kredītus.',
    storesConfirmOn:
      'Vai atļaut {app} jūsu vārdā lasīt veikalus tiešsaistē? Katra lasīšana izmanto {company} kredītus.',
    storesConfirmOff: 'Vai aizliegt {app} lasīt veikalus tiešsaistē?',
    storesEnabled: '{app} tagad var lasīt veikalus tiešsaistē.',
    storesDisabled: '{app} vairs nevar lasīt veikalus tiešsaistē.',
    mediaTitle: 'Mediju monitorings',
    mediaDescribe:
      'Meklējiet un citējiet teikto podkāstos un radio, ko apkopo Poneva mediju arhīvs.',
    mediaConfirmOn: 'Vai atļaut {app} jūsu vārdā meklēt {company} mediju monitoringā?',
    mediaConfirmOff: 'Vai aizliegt {app} meklēt mediju monitoringā?',
    mediaEnabled: '{app} tagad var meklēt mediju monitoringā.',
    mediaDisabled: '{app} vairs nevar meklēt mediju monitoringā.',
    // The two modes of a permission family.
    modeAsk: 'Jautāt katru reizi',
    modeAutomatic: 'Automātiski',
    modeGroupLabel: '{family}: apstiprināšana',
    askExplain: 'Katra darbība gaida jūs Iesūtnē, ja vien to neatļausiet tālāk.',
    automaticSave:
      '{app} saglabā darbu un jautā Poneva, nejautājot jums, tostarp lietas, ko nevar atsaukt.',
    automaticConnections:
      '{app} izmanto jūsu savienojumus, nejautājot jums, tostarp izmaiņām savienojumos, kas iestatīti uz Lasīšana un rakstīšana. Gmail un Outlook sūtījumi joprojām jautā jums.',
    confirmAutomatic: 'Vai ļaut {app} to darīt, nejautājot jums iepriekš?',
    modeAskSet: '{app} tagad jautā jums katru reizi.',
    modeAutomaticSet: '{app} tagad to dara, nejautājot.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Atļauts, nejautājot',
    allowedOf: '{allowed} no {total}',
    allowedNone: 'Vēl nav',
    allowedActions: 'zero:darbību|one:darbība|other:darbības',
    groupReadOnly: 'Iestatīts uz Lasīšana, tāpēc nekas nemainās',
    groupSendingAsks: 'Gmail un Outlook sūtījumi joprojām jautā jums',
    groupOff: 'Izslēgts',
    groupNothingToChange: 'Nav ko mainīt',
    groupUnavailable: 'Neizdevās ielādēt',
    showAll: 'Rādīt visas {count} darbības: {name}',
    showFewer: 'Rādīt mazāk',
    loading: 'Darbības tiek ielādētas…',
    loadFailed: 'Šīs darbības neizdevās ielādēt.',
    retry: 'Mēģināt vēlreiz',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Var atsaukt.',
    lockedNote: 'Vienmēr jautā jums.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Dažas lietas vienmēr jautā jums, ko arī izvēlētos: uzņēmuma īpašumtiesību nodošana, dalībnieka noņemšana, Lietotnes padarīšana publisku, naudas tērēšana ārpus kredītiem, {app} uzrakstītā Lietotnes koda apstiprināšana un Gmail vai Outlook sūtīšana. Darbības atļaušana nekad nedod {app} vairāk, nekā ir jums, un savienojums, kas iestatīts uz Lasīšana, paliek tikai lasāms.',
    errScopeNotGranted: '{app} šīs piekļuves vairs nav. Saraksts rāda pašreizējo stāvokli.',
    errNotAllowable: 'Šo darbību nevar atļaut, nejautājot.',
    errReadOnly: 'Šis savienojums ir iestatīts uz Lasīšana, tāpēc tajā neko nevar atļaut.',
    errNotSaved: 'Tas netika saglabāts. Nekas nemainījās.',
    errNotConfirmed: 'Izmaiņu neizdevās apstiprināt. Saraksts rāda pašreizējo stāvokli.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Saglabāt prezentāciju savā Bibliotēkā',
    action_save_document: 'Saglabāt dokumentu savā Bibliotēkā',
    action_save_table: 'Saglabāt tabulu savā Bibliotēkā',
    action_save_chart: 'Saglabāt diagrammu savā Bibliotēkā',
    action_save_page: 'Saglabāt lapu savā Bibliotēkā',
    action_add_to_poneva_files: 'Pievienot failu saviem Poneva failiem',
    action_save_poneva_skill_draft: 'Saglabāt Skill melnrakstu',
    action_import_agent_skill: 'Importēt Skill',
    action_evaluate_poneva_skill: 'Novērtēt Skill',
    action_ask_poneva: 'Pajautāt Poneva privātā tērzēšanā',
    action_continue_in_poneva: 'Turpināt Poneva tērzēšanā',
    action_design_shopping_assistant: 'Saglabāt iepirkšanās asistenta dizainu',
    action_storefront_preview_turn: 'Priekšskatīt iepirkšanās asistenta atbildi',
    action_test_shopping_assistant: 'Pārbaudīt iepirkšanās asistentu ar jautājumu',
    action_fix_shopping_assistant: 'Labot iepirkšanās asistentu',
    action_check_shopping_assistant_again: 'Vēlreiz pārbaudīt iepirkšanās asistentu',
    action_build_poneva_app: 'Izveidot Live App',
    action_delete_poneva_app: 'Dzēst Live App',
    action_update_item_sharing: 'Mainīt, ar ko tiek kopīgots vienums',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Izmanto kredītus.',
    note_storefront_preview_turn: 'Izmanto kredītus.',
    note_test_shopping_assistant: 'Izmanto kredītus.',
    note_fix_shopping_assistant: 'Izmanto kredītus.',
    note_check_shopping_assistant_again: 'Izmanto kredītus.',
    note_update_item_sharing: 'Viņi to redz uzreiz. Varat pārtraukt kopīgošanu.',
    note_delete_poneva_app: 'Nevar atsaukt.',
  },
  mt: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Użu tal-konnessjonijiet tiegħek',
    connDescribe:
      'Slack, applikazzjonijiet ta’ Pipedream, MCP tiegħek u Postgres. Bidliet biss fuq konnessjoni stabbilita fuq Qari u kitba.',
    connConfirmOn:
      'Ħalli lil {app} juża l-konnessjonijiet ta’ {company} bħalek? Dak kollu li jibdel xi ħaġa jistenniek fl-Inbox, sakemm ma tippermettix.',
    connConfirmOff: 'Twaqqaf lil {app} milli juża l-konnessjonijiet tiegħek?',
    connEnabled: '{app} issa jista’ juża l-konnessjonijiet tiegħek.',
    connDisabled: '{app} ma jistax jibqa’ juża l-konnessjonijiet tiegħek.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Aqra l-ħwienet live',
    storesDescribe:
      'Prezzijiet, stokk u dettalji tal-prodotti attwali minn ħwienet online veri, bl-għodod ċertifikati ta’ Poneva. Kull qari juża krediti.',
    storesConfirmOn:
      'Tħalli lil {app} jaqra l-ħwienet live f’ismek? Kull qari juża l-krediti ta’ {company}.',
    storesConfirmOff: 'Twaqqaf lil {app} milli jaqra l-ħwienet live?',
    storesEnabled: '{app} issa jista’ jaqra l-ħwienet live.',
    storesDisabled: '{app} ma jistax jibqa’ jaqra l-ħwienet live.',
    mediaTitle: 'Monitoraġġ tal-midja',
    mediaDescribe:
      'Fittex u kkwota dak li ntqal fil-podcasts u fir-radju li jiġbor l-arkivju tal-midja ta’ Poneva.',
    mediaConfirmOn: 'Tħalli lil {app} ifittex fil-monitoraġġ tal-midja ta’ {company} f’ismek?',
    mediaConfirmOff: 'Twaqqaf lil {app} milli jfittex fil-monitoraġġ tal-midja?',
    mediaEnabled: '{app} issa jista’ jfittex fil-monitoraġġ tal-midja.',
    mediaDisabled: '{app} ma jistax jibqa’ jfittex fil-monitoraġġ tal-midja.',
    // The two modes of a permission family.
    modeAsk: 'Staqsi kull darba',
    modeAutomatic: 'Awtomatiku',
    modeGroupLabel: '{family}: approvazzjoni',
    askExplain: 'Kull azzjoni tistenniek fl-Inbox, sakemm ma tippermettix hawn taħt.',
    automaticSave:
      '{app} jissejvja x-xogħol u jistaqsi lil Poneva mingħajr ma jistaqsik, inklużi affarijiet li ma jistgħux jiġu mħassra.',
    automaticConnections:
      '{app} juża l-konnessjonijiet tiegħek mingħajr ma jistaqsik, inklużi bidliet fuq konnessjonijiet stabbiliti fuq Qari u kitba. L-ibgħat minn Gmail u Outlook xorta jistaqsik.',
    confirmAutomatic: 'Ħalli lil {app} jagħmel dan mingħajr ma jistaqsik l-ewwel?',
    modeAskSet: '{app} issa jistaqsik kull darba.',
    modeAutomaticSet: '{app} issa jagħmel dan mingħajr ma jistaqsi.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Permess mingħajr ma jistaqsi',
    allowedOf: '{allowed} minn {total}',
    allowedNone: 'Għadu xejn',
    allowedActions:
      'one:azzjoni|two:azzjonijiet|few:azzjonijiet|many:azzjonijiet|other:azzjonijiet',
    groupReadOnly: 'Stabbilita fuq Qari, mela xejn ma jinbidel',
    groupSendingAsks: 'L-ibgħat minn Gmail u Outlook xorta jistaqsik',
    groupOff: 'Mitfi',
    groupNothingToChange: 'M’hemm xejn x’jibdel',
    groupUnavailable: 'Ma setgħetx titgħabba',
    showAll: 'Uri l-{count} azzjonijiet kollha ta’ {name}',
    showFewer: 'Uri inqas',
    loading: 'Qed jitgħabbew l-azzjonijiet…',
    loadFailed: 'Dawn l-azzjonijiet ma setgħux jitgħabbew.',
    retry: 'Erġa’ pprova',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Tista’ titneħħa.',
    lockedNote: 'Dejjem jistaqsik.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Xi affarijiet dejjem jistaqsuk, ikun xi jkun li tagħżel: it-trasferiment tas-sjieda tal-kumpanija, it-tneħħija ta’ membru, li tagħmel Applikazzjoni pubblika, nefqa ta’ flus lil hinn mill-krediti, il-konferma tal-kodiċi tal-Applikazzjoni miktub minn {app}, u l-ibgħat minn Gmail jew Outlook. Li tippermetti azzjoni qatt ma jagħti lil {app} aktar minn dak li għandek int, u konnessjoni stabbilita fuq Qari tibqa’ għall-qari biss.',
    errScopeNotGranted: '{app} m’għadux għandu din l-aċċess. Il-lista turi l-istat kurrenti.',
    errNotAllowable: 'Dik l-azzjoni ma tistax tiġi permessa mingħajr ma jsir mistoqsija.',
    errReadOnly:
      'Dik il-konnessjoni hija stabbilita fuq Qari, mela xejn fuqha ma jista’ jiġi permess.',
    errNotSaved: 'Ma ġiex issejvjat. Xejn ma nbidel.',
    errNotConfirmed: 'Din il-bidla ma setgħetx tiġi kkonfermata. Il-lista turi l-istat kurrenti.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Issejvja preżentazzjoni fil-Librerija tiegħek',
    action_save_document: 'Issejvja dokument fil-Librerija tiegħek',
    action_save_table: 'Issejvja tabella fil-Librerija tiegħek',
    action_save_chart: 'Issejvja grafika fil-Librerija tiegħek',
    action_save_page: 'Issejvja paġna fil-Librerija tiegħek',
    action_add_to_poneva_files: 'Żid fajl mal-fajls tiegħek ta’ Poneva',
    action_save_poneva_skill_draft: 'Issejvja abbozz ta’ Skill',
    action_import_agent_skill: 'Importa Skill',
    action_evaluate_poneva_skill: 'Evalwa Skill',
    action_ask_poneva: 'Staqsi lil Poneva f’ċat privat',
    action_continue_in_poneva: 'Kompli f’ċat ta’ Poneva',
    action_design_shopping_assistant: 'Issejvja d-disinn ta’ assistent tax-xiri',
    action_storefront_preview_turn: 'Previżjoni tat-tweġiba ta’ assistent tax-xiri',
    action_test_shopping_assistant: 'Ittestja assistent tax-xiri b’mistoqsija',
    action_fix_shopping_assistant: 'Irranġa assistent tax-xiri',
    action_check_shopping_assistant_again: 'Iċċekkja assistent tax-xiri mill-ġdid',
    action_build_poneva_app: 'Ibni Live App',
    action_delete_poneva_app: 'Ħassar Live App',
    action_update_item_sharing: 'Biddel ma’ min huwa kondiviż element',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Juża krediti.',
    note_storefront_preview_turn: 'Juża krediti.',
    note_test_shopping_assistant: 'Juża krediti.',
    note_fix_shopping_assistant: 'Juża krediti.',
    note_check_shopping_assistant_again: 'Juża krediti.',
    note_update_item_sharing: 'Jaraw dan minnufih. Tista’ twaqqaf il-kondiviżjoni.',
    note_delete_poneva_app: 'Ma tistax titneħħa.',
  },
  nl: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Je verbindingen gebruiken',
    connDescribe:
      'Slack, Pipedream-apps, eigen MCP en Postgres. Wijzigingen alleen op een verbinding die op Lezen en schrijven staat.',
    connConfirmOn:
      'Wil je dat {app} de verbindingen van {company} als jou gebruikt? Alles wat iets wijzigt, wacht in je Inbox op je, tenzij je het toestaat.',
    connConfirmOff: 'Wil je dat {app} je verbindingen niet meer gebruikt?',
    connEnabled: '{app} kan nu je verbindingen gebruiken.',
    connDisabled: '{app} kan je verbindingen niet meer gebruiken.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Winkels live lezen',
    storesDescribe:
      'Actuele prijzen, voorraad en productgegevens van echte webwinkels, met de gecertificeerde winkeltools van Poneva. Elke leesactie kost credits.',
    storesConfirmOn:
      '{app} toestaan winkels live namens jou te lezen? Elke leesactie kost credits van {company}.',
    storesConfirmOff: '{app} niet langer winkels live laten lezen?',
    storesEnabled: '{app} kan nu winkels live lezen.',
    storesDisabled: '{app} kan geen winkels meer live lezen.',
    mediaTitle: 'Mediamonitoring',
    mediaDescribe:
      'Zoek en citeer wat er is gezegd in de podcasts en op de radio die het media-archief van Poneva verzamelt.',
    mediaConfirmOn: '{app} toestaan namens jou te zoeken in de mediamonitoring van {company}?',
    mediaConfirmOff: '{app} niet langer laten zoeken in de mediamonitoring?',
    mediaEnabled: '{app} kan nu zoeken in de mediamonitoring.',
    mediaDisabled: '{app} kan niet meer zoeken in de mediamonitoring.',
    // The two modes of a permission family.
    modeAsk: 'Elke keer vragen',
    modeAutomatic: 'Automatisch',
    modeGroupLabel: '{family}: goedkeuring',
    askExplain: 'Elke actie wacht in je Inbox op je, tenzij je die hieronder toestaat.',
    automaticSave:
      '{app} slaat werk op en vraagt Poneva zonder het jou te vragen, ook dingen die niet ongedaan te maken zijn.',
    automaticConnections:
      '{app} gebruikt je verbindingen zonder het jou te vragen, ook voor wijzigingen op verbindingen die op Lezen en schrijven staan. Versturen via Gmail en Outlook vraagt nog steeds aan jou.',
    confirmAutomatic: 'Wil je dat {app} dit doet zonder het eerst te vragen?',
    modeAskSet: '{app} vraagt het je nu elke keer.',
    modeAutomaticSet: '{app} doet dit nu zonder te vragen.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Toegestaan zonder te vragen',
    allowedOf: '{allowed} van {total}',
    allowedNone: 'Nog geen',
    allowedActions: 'one:actie|other:acties',
    groupReadOnly: 'Op Lezen gezet, dus er verandert niets',
    groupSendingAsks: 'Versturen via Gmail en Outlook vraagt nog steeds aan jou',
    groupOff: 'Uitgeschakeld',
    groupNothingToChange: 'Niets om te wijzigen',
    groupUnavailable: 'Kon niet laden',
    showAll: 'Alle {count} acties van {name} tonen',
    showFewer: 'Minder tonen',
    loading: 'Acties laden…',
    loadFailed: 'De acties konden niet worden geladen.',
    retry: 'Opnieuw proberen',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Kan ongedaan worden gemaakt.',
    lockedNote: 'Vraagt altijd aan jou.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Sommige dingen vragen het altijd aan jou, wat je ook kiest: het eigendom van het bedrijf overdragen, een lid verwijderen, een App openbaar maken, geld uitgeven boven credits, App-code bevestigen die {app} heeft geschreven en versturen via Gmail of Outlook. Een actie toestaan geeft {app} nooit meer dan jij hebt, en een verbinding op Lezen blijft alleen-lezen.',
    errScopeNotGranted: '{app} heeft deze toegang niet meer. De lijst toont de huidige stand.',
    errNotAllowable: 'Die actie kan niet zonder vragen worden toegestaan.',
    errReadOnly: 'Die verbinding staat op Lezen, dus er kan niets op worden toegestaan.',
    errNotSaved: 'Dat is niet opgeslagen. Er is niets veranderd.',
    errNotConfirmed: 'Die wijziging kon niet worden bevestigd. De lijst toont de huidige stand.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Een presentatie opslaan in je Bibliotheek',
    action_save_document: 'Een document opslaan in je Bibliotheek',
    action_save_table: 'Een tabel opslaan in je Bibliotheek',
    action_save_chart: 'Een grafiek opslaan in je Bibliotheek',
    action_save_page: 'Een pagina opslaan in je Bibliotheek',
    action_add_to_poneva_files: 'Een bestand toevoegen aan je Poneva-bestanden',
    action_save_poneva_skill_draft: 'Een conceptversie van een Skill opslaan',
    action_import_agent_skill: 'Een Skill importeren',
    action_evaluate_poneva_skill: 'Een Skill beoordelen',
    action_ask_poneva: 'Poneva vragen in een privéchat',
    action_continue_in_poneva: 'Verdergaan in een Poneva-chat',
    action_design_shopping_assistant: 'Het ontwerp van een winkelassistent opslaan',
    action_storefront_preview_turn: 'De reactie van een winkelassistent bekijken',
    action_test_shopping_assistant: 'Een winkelassistent testen met een vraag',
    action_fix_shopping_assistant: 'Een winkelassistent herstellen',
    action_check_shopping_assistant_again: 'Een winkelassistent opnieuw controleren',
    action_build_poneva_app: 'Een Live App bouwen',
    action_delete_poneva_app: 'Een Live App verwijderen',
    action_update_item_sharing: 'Wijzigen met wie een item wordt gedeeld',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Gebruikt credits.',
    note_storefront_preview_turn: 'Gebruikt credits.',
    note_test_shopping_assistant: 'Gebruikt credits.',
    note_fix_shopping_assistant: 'Gebruikt credits.',
    note_check_shopping_assistant_again: 'Gebruikt credits.',
    note_update_item_sharing: 'Ze zien het meteen. Je kunt het delen stoppen.',
    note_delete_poneva_app: 'Kan niet ongedaan worden gemaakt.',
  },
  pl: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Korzystanie z Twoich połączeń',
    connDescribe:
      'Slack, aplikacje Pipedream, własny MCP i Postgres. Zmiany tylko na połączeniu ustawionym na Odczyt i zapis.',
    connConfirmOn:
      'Pozwolić, aby {app} korzystał z połączeń firmy {company} jako Ty? Wszystko, co coś zmienia, czeka na Ciebie w Skrzynce, chyba że na to pozwolisz.',
    connConfirmOff: 'Zabronić {app} korzystania z Twoich połączeń?',
    connEnabled: '{app} może teraz korzystać z Twoich połączeń.',
    connDisabled: '{app} nie może już korzystać z Twoich połączeń.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Czytanie sklepów na żywo',
    storesDescribe:
      'Aktualne ceny, stany magazynowe i szczegóły produktów z prawdziwych sklepów internetowych, z certyfikowanymi narzędziami Poneva. Każdy odczyt zużywa kredyty.',
    storesConfirmOn:
      'Pozwolić {app} czytać sklepy na żywo w Twoim imieniu? Każdy odczyt zużywa kredyty firmy {company}.',
    storesConfirmOff: 'Zablokować {app} czytanie sklepów na żywo?',
    storesEnabled: '{app} może teraz czytać sklepy na żywo.',
    storesDisabled: '{app} nie może już czytać sklepów na żywo.',
    mediaTitle: 'Monitoring mediów',
    mediaDescribe:
      'Wyszukuj i cytuj to, co powiedziano w podcastach i w radiu, które gromadzi archiwum mediów Poneva.',
    mediaConfirmOn:
      'Pozwolić {app} przeszukiwać monitoring mediów firmy {company} w Twoim imieniu?',
    mediaConfirmOff: 'Zablokować {app} przeszukiwanie monitoringu mediów?',
    mediaEnabled: '{app} może teraz przeszukiwać monitoring mediów.',
    mediaDisabled: '{app} nie może już przeszukiwać monitoringu mediów.',
    // The two modes of a permission family.
    modeAsk: 'Pytaj za każdym razem',
    modeAutomatic: 'Automatycznie',
    modeGroupLabel: '{family}: zatwierdzanie',
    askExplain: 'Każde działanie czeka na Ciebie w Skrzynce, chyba że pozwolisz na nie poniżej.',
    automaticSave:
      '{app} zapisuje pracę i pyta Ponevę bez pytania Ciebie, także o rzeczach, których nie da się cofnąć.',
    automaticConnections:
      '{app} korzysta z Twoich połączeń bez pytania Ciebie, także przy zmianach na połączeniach ustawionych na Odczyt i zapis. Wysyłanie z Gmaila i Outlooka nadal pyta Ciebie.',
    confirmAutomatic: 'Pozwolić, aby {app} robił to bez wcześniejszego pytania?',
    modeAskSet: '{app} pyta Cię teraz za każdym razem.',
    modeAutomaticSet: '{app} robi to teraz bez pytania.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Dozwolone bez pytania',
    allowedOf: '{allowed} z {total}',
    allowedNone: 'Jeszcze brak',
    allowedActions: 'one:działanie|few:działania|many:działań|other:działania',
    groupReadOnly: 'Ustawione na Odczyt, więc nic się nie zmienia',
    groupSendingAsks: 'Wysyłanie z Gmaila i Outlooka nadal pyta Ciebie',
    groupOff: 'Wyłączone',
    groupNothingToChange: 'Nie ma nic do zmiany',
    groupUnavailable: 'Nie udało się wczytać',
    showAll: 'Pokaż wszystkie działania ({count}) w {name}',
    showFewer: 'Pokaż mniej',
    loading: 'Wczytywanie działań…',
    loadFailed: 'Nie udało się wczytać tych działań.',
    retry: 'Spróbuj ponownie',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Można cofnąć.',
    lockedNote: 'Zawsze pyta Ciebie.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Niektóre rzeczy zawsze pytają Ciebie, cokolwiek wybierzesz: przekazanie własności firmy, usunięcie członka, upublicznienie Aplikacji, wydawanie pieniędzy ponad kredyty, potwierdzanie kodu Aplikacji napisanego przez {app} oraz wysyłanie z Gmaila lub Outlooka. Zezwolenie na działanie nigdy nie daje {app} więcej, niż masz Ty, a połączenie ustawione na Odczyt pozostaje tylko do odczytu.',
    errScopeNotGranted: '{app} nie ma już tego dostępu. Lista pokazuje aktualny stan.',
    errNotAllowable: 'Na to działanie nie można pozwolić bez pytania.',
    errReadOnly: 'To połączenie jest ustawione na Odczyt, więc nic na nim nie można dopuścić.',
    errNotSaved: 'Nie zapisano. Nic się nie zmieniło.',
    errNotConfirmed: 'Nie udało się potwierdzić tej zmiany. Lista pokazuje aktualny stan.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Zapisz prezentację w Bibliotece',
    action_save_document: 'Zapisz dokument w Bibliotece',
    action_save_table: 'Zapisz tabelę w Bibliotece',
    action_save_chart: 'Zapisz wykres w Bibliotece',
    action_save_page: 'Zapisz stronę w Bibliotece',
    action_add_to_poneva_files: 'Dodaj plik do plików Poneva',
    action_save_poneva_skill_draft: 'Zapisz wersję roboczą Skilla',
    action_import_agent_skill: 'Zaimportuj Skill',
    action_evaluate_poneva_skill: 'Oceń Skill',
    action_ask_poneva: 'Zapytaj Ponevę w prywatnym czacie',
    action_continue_in_poneva: 'Kontynuuj w czacie Poneva',
    action_design_shopping_assistant: 'Zapisz projekt asystenta zakupowego',
    action_storefront_preview_turn: 'Podgląd odpowiedzi asystenta zakupowego',
    action_test_shopping_assistant: 'Przetestuj asystenta zakupowego pytaniem',
    action_fix_shopping_assistant: 'Napraw asystenta zakupowego',
    action_check_shopping_assistant_again: 'Sprawdź asystenta zakupowego ponownie',
    action_build_poneva_app: 'Zbuduj Live App',
    action_delete_poneva_app: 'Usuń Live App',
    action_update_item_sharing: 'Zmień, z kim udostępniono element',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Zużywa kredyty.',
    note_storefront_preview_turn: 'Zużywa kredyty.',
    note_test_shopping_assistant: 'Zużywa kredyty.',
    note_fix_shopping_assistant: 'Zużywa kredyty.',
    note_check_shopping_assistant_again: 'Zużywa kredyty.',
    note_update_item_sharing: 'Widzą to od razu. Możesz przestać udostępniać.',
    note_delete_poneva_app: 'Nie można cofnąć.',
  },
  pt: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Usar as suas ligações',
    connDescribe:
      'Slack, apps do Pipedream, MCP próprio e Postgres. As alterações só acontecem numa ligação definida como Leitura e escrita.',
    connConfirmOn:
      'Deixar {app} usar as ligações de {company} como si? Tudo o que altere algo aguarda por si na Caixa de entrada, a menos que o permita.',
    connConfirmOff: 'Impedir {app} de usar as suas ligações?',
    connEnabled: '{app} já pode usar as suas ligações.',
    connDisabled: '{app} já não pode usar as suas ligações.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Ler lojas em direto',
    storesDescribe:
      'Preços, stock e detalhes de produtos atuais de lojas online reais, com as ferramentas certificadas da Poneva. Cada leitura usa créditos.',
    storesConfirmOn:
      'Permitir que {app} leia lojas em direto em seu nome? Cada leitura usa créditos da {company}.',
    storesConfirmOff: 'Impedir que {app} leia lojas em direto?',
    storesEnabled: '{app} já pode ler lojas em direto.',
    storesDisabled: '{app} já não pode ler lojas em direto.',
    mediaTitle: 'Monitorização de media',
    mediaDescribe:
      'Pesquise e cite o que foi dito nos podcasts e na rádio que o arquivo de media da Poneva recolhe.',
    mediaConfirmOn:
      'Permitir que {app} pesquise na monitorização de media da {company} em seu nome?',
    mediaConfirmOff: 'Impedir que {app} pesquise na monitorização de media?',
    mediaEnabled: '{app} já pode pesquisar na monitorização de media.',
    mediaDisabled: '{app} já não pode pesquisar na monitorização de media.',
    // The two modes of a permission family.
    modeAsk: 'Perguntar sempre',
    modeAutomatic: 'Automático',
    modeGroupLabel: '{family}: aprovação',
    askExplain: 'Cada ação aguarda por si na Caixa de entrada, a menos que a permita abaixo.',
    automaticSave:
      '{app} guarda trabalho e pergunta à Poneva sem lhe perguntar, incluindo coisas que não se podem anular.',
    automaticConnections:
      '{app} usa as suas ligações sem lhe perguntar, incluindo alterações em ligações definidas como Leitura e escrita. Os envios do Gmail e do Outlook continuam a perguntar-lhe.',
    confirmAutomatic: 'Deixar {app} fazer isto sem lhe perguntar primeiro?',
    modeAskSet: '{app} passa a perguntar-lhe sempre.',
    modeAutomaticSet: '{app} passa a fazer isto sem perguntar.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Permitido sem perguntar',
    allowedOf: '{allowed} de {total}',
    allowedNone: 'Nenhuma ainda',
    allowedActions: 'one:ação|other:ações',
    groupReadOnly: 'Definida como Leitura, por isso nada muda',
    groupSendingAsks: 'Os envios do Gmail e do Outlook continuam a perguntar-lhe',
    groupOff: 'Desativada',
    groupNothingToChange: 'Nada que possa alterar',
    groupUnavailable: 'Não foi possível carregar',
    showAll: 'Mostrar as {count} ações de {name}',
    showFewer: 'Mostrar menos',
    loading: 'A carregar ações…',
    loadFailed: 'Não foi possível carregar estas ações.',
    retry: 'Tentar novamente',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Pode ser anulado.',
    lockedNote: 'Pergunta-lhe sempre.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Algumas coisas perguntam-lhe sempre, seja qual for a sua escolha: transferir a propriedade da empresa, remover um membro, tornar uma App pública, gastar dinheiro além dos créditos, confirmar o código de App que {app} escreveu e os envios do Gmail ou do Outlook. Permitir uma ação nunca dá a {app} mais do que tem, e uma ligação definida como Leitura continua só de leitura.',
    errScopeNotGranted: '{app} já não tem este acesso. A lista mostra o estado atual.',
    errNotAllowable: 'Essa ação não pode ser permitida sem perguntar.',
    errReadOnly: 'Essa ligação está definida como Leitura, por isso nada nela pode ser permitido.',
    errNotSaved: 'Não foi guardado. Nada mudou.',
    errNotConfirmed: 'Não foi possível confirmar essa alteração. A lista mostra o estado atual.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Guardar uma apresentação na sua Biblioteca',
    action_save_document: 'Guardar um documento na sua Biblioteca',
    action_save_table: 'Guardar uma tabela na sua Biblioteca',
    action_save_chart: 'Guardar um gráfico na sua Biblioteca',
    action_save_page: 'Guardar uma página na sua Biblioteca',
    action_add_to_poneva_files: 'Adicionar um ficheiro aos seus ficheiros Poneva',
    action_save_poneva_skill_draft: 'Guardar o rascunho de uma Skill',
    action_import_agent_skill: 'Importar uma Skill',
    action_evaluate_poneva_skill: 'Avaliar uma Skill',
    action_ask_poneva: 'Perguntar à Poneva numa conversa privada',
    action_continue_in_poneva: 'Continuar numa conversa Poneva',
    action_design_shopping_assistant: 'Guardar o design de um assistente de compras',
    action_storefront_preview_turn: 'Pré-visualizar a resposta de um assistente de compras',
    action_test_shopping_assistant: 'Testar um assistente de compras com uma pergunta',
    action_fix_shopping_assistant: 'Corrigir um assistente de compras',
    action_check_shopping_assistant_again: 'Verificar de novo um assistente de compras',
    action_build_poneva_app: 'Criar uma Live App',
    action_delete_poneva_app: 'Eliminar uma Live App',
    action_update_item_sharing: 'Alterar com quem um item é partilhado',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Usa créditos.',
    note_storefront_preview_turn: 'Usa créditos.',
    note_test_shopping_assistant: 'Usa créditos.',
    note_fix_shopping_assistant: 'Usa créditos.',
    note_check_shopping_assistant_again: 'Usa créditos.',
    note_update_item_sharing: 'Veem-no de imediato. Pode deixar de partilhar.',
    note_delete_poneva_app: 'Não pode ser anulado.',
  },
  ro: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Folosirea conexiunilor tale',
    connDescribe:
      'Slack, aplicații Pipedream, MCP propriu și Postgres. Modificările au loc doar pe o conexiune setată pe Citire și scriere.',
    connConfirmOn:
      'Lași {app} să folosească conexiunile {company} ca tine? Tot ce modifică ceva te așteaptă în Inbox, dacă nu permiți.',
    connConfirmOff: 'Oprești {app} să-ți folosească conexiunile?',
    connEnabled: '{app} îți poate folosi acum conexiunile.',
    connDisabled: '{app} nu-ți mai poate folosi conexiunile.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Citirea magazinelor în direct',
    storesDescribe:
      'Prețuri, stocuri și detalii de produs actuale din magazine online reale, cu instrumentele certificate Poneva. Fiecare citire folosește credite.',
    storesConfirmOn:
      'Lași {app} să citească magazinele în direct în numele tău? Fiecare citire folosește creditele {company}.',
    storesConfirmOff: 'Oprești {app} să mai citească magazinele în direct?',
    storesEnabled: '{app} poate acum citi magazinele în direct.',
    storesDisabled: '{app} nu mai poate citi magazinele în direct.',
    mediaTitle: 'Monitorizare media',
    mediaDescribe:
      'Caută și citează ce s-a spus în podcasturile și emisiunile radio pe care le colectează arhiva media Poneva.',
    mediaConfirmOn: 'Lași {app} să caute în monitorizarea media a {company} în numele tău?',
    mediaConfirmOff: 'Oprești {app} să mai caute în monitorizarea media?',
    mediaEnabled: '{app} poate acum căuta în monitorizarea media.',
    mediaDisabled: '{app} nu mai poate căuta în monitorizarea media.',
    // The two modes of a permission family.
    modeAsk: 'Întreabă de fiecare dată',
    modeAutomatic: 'Automat',
    modeGroupLabel: '{family}: aprobare',
    askExplain: 'Fiecare acțiune te așteaptă în Inbox, dacă nu o permiți mai jos.',
    automaticSave:
      '{app} salvează munca și îl întreabă pe Poneva fără să te întrebe, inclusiv lucruri care nu pot fi anulate.',
    automaticConnections:
      '{app} folosește conexiunile tale fără să te întrebe, inclusiv pentru modificări pe conexiuni setate pe Citire și scriere. Trimiterile din Gmail și Outlook te întreabă în continuare.',
    confirmAutomatic: 'Lași {app} să facă asta fără să te întrebe mai întâi?',
    modeAskSet: '{app} te întreabă acum de fiecare dată.',
    modeAutomaticSet: '{app} face acum asta fără să întrebe.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Permis fără întrebare',
    allowedOf: '{allowed} din {total}',
    allowedNone: 'Încă niciuna',
    allowedActions: 'one:acțiune|few:acțiuni|other:de acțiuni',
    groupReadOnly: 'Setată pe Citire, deci nu se schimbă nimic',
    groupSendingAsks: 'Trimiterile din Gmail și Outlook te întreabă în continuare',
    groupOff: 'Dezactivată',
    groupNothingToChange: 'Nu are nimic de schimbat',
    groupUnavailable: 'Nu s-a putut încărca',
    showAll: 'Arată toate cele {count} acțiuni din {name}',
    showFewer: 'Arată mai puține',
    loading: 'Se încarcă acțiunile…',
    loadFailed: 'Aceste acțiuni nu au putut fi încărcate.',
    retry: 'Încearcă din nou',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Poate fi anulat.',
    lockedNote: 'Te întreabă mereu.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Unele lucruri te întreabă mereu, indiferent ce alegi: transferul proprietății companiei, eliminarea unui membru, publicarea unei Aplicații, cheltuirea de bani peste credite, confirmarea codului de Aplicație scris de {app} și trimiterile din Gmail sau Outlook. Permiterea unei acțiuni nu îi dă niciodată lui {app} mai mult decât ai tu, iar o conexiune setată pe Citire rămâne doar pentru citire.',
    errScopeNotGranted: '{app} nu mai are acest acces. Lista arată starea curentă.',
    errNotAllowable: 'Acea acțiune nu poate fi permisă fără întrebare.',
    errReadOnly: 'Acea conexiune este setată pe Citire, deci nimic pe ea nu poate fi permis.',
    errNotSaved: 'Nu s-a salvat. Nu s-a schimbat nimic.',
    errNotConfirmed: 'Schimbarea nu a putut fi confirmată. Lista arată starea curentă.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Salvează o prezentare în Bibliotecă',
    action_save_document: 'Salvează un document în Bibliotecă',
    action_save_table: 'Salvează un tabel în Bibliotecă',
    action_save_chart: 'Salvează un grafic în Bibliotecă',
    action_save_page: 'Salvează o pagină în Bibliotecă',
    action_add_to_poneva_files: 'Adaugă un fișier la fișierele Poneva',
    action_save_poneva_skill_draft: 'Salvează ciorna unui Skill',
    action_import_agent_skill: 'Importă un Skill',
    action_evaluate_poneva_skill: 'Evaluează un Skill',
    action_ask_poneva: 'Întreabă-l pe Poneva într-un chat privat',
    action_continue_in_poneva: 'Continuă într-un chat Poneva',
    action_design_shopping_assistant: 'Salvează designul unui asistent de cumpărături',
    action_storefront_preview_turn: 'Previzualizează răspunsul unui asistent de cumpărături',
    action_test_shopping_assistant: 'Testează un asistent de cumpărături cu o întrebare',
    action_fix_shopping_assistant: 'Repară un asistent de cumpărături',
    action_check_shopping_assistant_again: 'Verifică din nou un asistent de cumpărături',
    action_build_poneva_app: 'Construiește o Live App',
    action_delete_poneva_app: 'Șterge o Live App',
    action_update_item_sharing: 'Schimbă cu cine este distribuit un element',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Folosește credite.',
    note_storefront_preview_turn: 'Folosește credite.',
    note_test_shopping_assistant: 'Folosește credite.',
    note_fix_shopping_assistant: 'Folosește credite.',
    note_check_shopping_assistant_again: 'Folosește credite.',
    note_update_item_sharing: 'Îl văd imediat. Poți opri distribuirea.',
    note_delete_poneva_app: 'Nu poate fi anulat.',
  },
  sk: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Používať vaše pripojenia',
    connDescribe:
      'Slack, aplikácie Pipedream, vlastný MCP a Postgres. Zmeny len na pripojení nastavenom na Čítanie a zápis.',
    connConfirmOn:
      'Nechať {app} používať pripojenia firmy {company} ako vás? Všetko, čo niečo mení, na vás čaká v Schránke, ak to nepovolíte.',
    connConfirmOff: 'Zakázať {app} používať vaše pripojenia?',
    connEnabled: '{app} teraz môže používať vaše pripojenia.',
    connDisabled: '{app} už nemôže používať vaše pripojenia.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Čítanie obchodov naživo',
    storesDescribe:
      'Aktuálne ceny, zásoby a údaje o produktoch zo skutočných e-shopov pomocou certifikovaných nástrojov Poneva. Každé čítanie spotrebuje kredity.',
    storesConfirmOn:
      'Povoliť aplikácii {app} čítať obchody naživo vo vašom mene? Každé čítanie spotrebuje kredity spoločnosti {company}.',
    storesConfirmOff: 'Zakázať aplikácii {app} čítanie obchodov naživo?',
    storesEnabled: '{app} teraz môže čítať obchody naživo.',
    storesDisabled: '{app} už nemôže čítať obchody naživo.',
    mediaTitle: 'Monitoring médií',
    mediaDescribe:
      'Vyhľadávanie a citovanie toho, čo zaznelo v podcastoch a v rádiu, ktoré zhromažďuje mediálny archív Poneva.',
    mediaConfirmOn:
      'Povoliť aplikácii {app} prehľadávať monitoring médií spoločnosti {company} vo vašom mene?',
    mediaConfirmOff: 'Zakázať aplikácii {app} prehľadávanie monitoringu médií?',
    mediaEnabled: '{app} teraz môže prehľadávať monitoring médií.',
    mediaDisabled: '{app} už nemôže prehľadávať monitoring médií.',
    // The two modes of a permission family.
    modeAsk: 'Opýtať sa vždy',
    modeAutomatic: 'Automaticky',
    modeGroupLabel: '{family}: schvaľovanie',
    askExplain: 'Každá akcia na vás čaká v Schránke, ak ju nepovolíte nižšie.',
    automaticSave:
      '{app} ukladá prácu a pýta sa Ponevy bez opýtania sa vás, vrátane vecí, ktoré sa nedajú vrátiť späť.',
    automaticConnections:
      '{app} používa vaše pripojenia bez opýtania sa vás, vrátane zmien na pripojeniach nastavených na Čítanie a zápis. Odosielanie z Gmailu a Outlooku sa stále pýta vás.',
    confirmAutomatic: 'Nechať {app} urobiť to bez predchádzajúcej otázky?',
    modeAskSet: '{app} sa vás teraz pýta vždy.',
    modeAutomaticSet: '{app} to teraz robí bez otázky.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Povolené bez otázky',
    allowedOf: '{allowed} z {total}',
    allowedNone: 'Zatiaľ žiadne',
    allowedActions: 'one:akcia|few:akcie|many:akcie|other:akcií',
    groupReadOnly: 'Nastavené na Čítanie, takže sa nič nemení',
    groupSendingAsks: 'Odosielanie z Gmailu a Outlooku sa stále pýta vás',
    groupOff: 'Vypnuté',
    groupNothingToChange: 'Nie je čo meniť',
    groupUnavailable: 'Nepodarilo sa načítať',
    showAll: 'Zobraziť všetkých {count} akcií v {name}',
    showFewer: 'Zobraziť menej',
    loading: 'Načítavajú sa akcie…',
    loadFailed: 'Tieto akcie sa nepodarilo načítať.',
    retry: 'Skúsiť znova',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Dá sa vrátiť späť.',
    lockedNote: 'Vždy sa pýta vás.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Niektoré veci sa vždy pýtajú vás, nech si vyberiete čokoľvek: prevod vlastníctva firmy, odstránenie člena, zverejnenie Aplikácie, míňanie peňazí nad rámec kreditov, potvrdenie kódu Aplikácie, ktorý napísal {app}, a odosielanie z Gmailu alebo Outlooku. Povolenie akcie nikdy nedá {app} viac, než máte vy, a pripojenie nastavené na Čítanie zostáva len na čítanie.',
    errScopeNotGranted: '{app} už tento prístup nemá. Zoznam ukazuje aktuálny stav.',
    errNotAllowable: 'Túto akciu nemožno povoliť bez otázky.',
    errReadOnly: 'Toto pripojenie je nastavené na Čítanie, takže na ňom nemožno nič povoliť.',
    errNotSaved: 'Neuložilo sa to. Nič sa nezmenilo.',
    errNotConfirmed: 'Túto zmenu sa nepodarilo potvrdiť. Zoznam ukazuje aktuálny stav.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Uložiť prezentáciu do Knižnice',
    action_save_document: 'Uložiť dokument do Knižnice',
    action_save_table: 'Uložiť tabuľku do Knižnice',
    action_save_chart: 'Uložiť graf do Knižnice',
    action_save_page: 'Uložiť stránku do Knižnice',
    action_add_to_poneva_files: 'Pridať súbor medzi súbory Poneva',
    action_save_poneva_skill_draft: 'Uložiť koncept Skillu',
    action_import_agent_skill: 'Importovať Skill',
    action_evaluate_poneva_skill: 'Vyhodnotiť Skill',
    action_ask_poneva: 'Opýtať sa Ponevy v súkromnom chate',
    action_continue_in_poneva: 'Pokračovať v chate Poneva',
    action_design_shopping_assistant: 'Uložiť návrh nákupného asistenta',
    action_storefront_preview_turn: 'Náhľad odpovede nákupného asistenta',
    action_test_shopping_assistant: 'Otestovať nákupného asistenta otázkou',
    action_fix_shopping_assistant: 'Opraviť nákupného asistenta',
    action_check_shopping_assistant_again: 'Znova skontrolovať nákupného asistenta',
    action_build_poneva_app: 'Vytvoriť Live App',
    action_delete_poneva_app: 'Odstrániť Live App',
    action_update_item_sharing: 'Zmeniť, s kým je položka zdieľaná',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Spotrebúva kredity.',
    note_storefront_preview_turn: 'Spotrebúva kredity.',
    note_test_shopping_assistant: 'Spotrebúva kredity.',
    note_fix_shopping_assistant: 'Spotrebúva kredity.',
    note_check_shopping_assistant_again: 'Spotrebúva kredity.',
    note_update_item_sharing: 'Vidia to hneď. Zdieľanie môžete ukončiť.',
    note_delete_poneva_app: 'Nedá sa vrátiť späť.',
  },
  sl: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Uporaba vaših povezav',
    connDescribe:
      'Slack, aplikacije Pipedream, lasten MCP in Postgres. Spremembe samo na povezavi, nastavljeni na Branje in pisanje.',
    connConfirmOn:
      'Naj {app} uporablja povezave podjetja {company} kot vi? Vse, kar kaj spremeni, čaka na vas v Nabiralniku, razen če to dovolite.',
    connConfirmOff: 'Naj {app} ne uporablja več vaših povezav?',
    connEnabled: '{app} zdaj lahko uporablja vaše povezave.',
    connDisabled: '{app} ne more več uporabljati vaših povezav.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Branje trgovin v živo',
    storesDescribe:
      'Trenutne cene, zaloga in podrobnosti izdelkov iz pravih spletnih trgovin s certificiranimi orodji Poneva. Vsako branje porabi dobropise.',
    storesConfirmOn:
      'Ali dovolite aplikaciji {app}, da v vašem imenu bere trgovine v živo? Vsako branje porabi dobropise podjetja {company}.',
    storesConfirmOff: 'Ali aplikaciji {app} prepoveste branje trgovin v živo?',
    storesEnabled: '{app} lahko zdaj bere trgovine v živo.',
    storesDisabled: '{app} ne more več brati trgovin v živo.',
    mediaTitle: 'Spremljanje medijev',
    mediaDescribe:
      'Iščite in citirajte, kar je bilo povedano v podkastih in na radiu, ki jih zbira medijski arhiv Poneva.',
    mediaConfirmOn:
      'Ali dovolite aplikaciji {app}, da v vašem imenu išče po spremljanju medijev podjetja {company}?',
    mediaConfirmOff: 'Ali aplikaciji {app} prepoveste iskanje po spremljanju medijev?',
    mediaEnabled: '{app} lahko zdaj išče po spremljanju medijev.',
    mediaDisabled: '{app} ne more več iskati po spremljanju medijev.',
    // The two modes of a permission family.
    modeAsk: 'Vprašaj vsakič',
    modeAutomatic: 'Samodejno',
    modeGroupLabel: '{family}: odobritev',
    askExplain: 'Vsako dejanje čaka na vas v Nabiralniku, razen če ga dovolite spodaj.',
    automaticSave:
      '{app} shranjuje delo in sprašuje Poneva, ne da bi vprašal vas, tudi stvari, ki jih ni mogoče razveljaviti.',
    automaticConnections:
      '{app} uporablja vaše povezave, ne da bi vprašal vas, tudi za spremembe na povezavah, nastavljenih na Branje in pisanje. Pošiljanje iz Gmaila in Outlooka še vedno vpraša vas.',
    confirmAutomatic: 'Naj {app} to naredi, ne da bi vas prej vprašal?',
    modeAskSet: '{app} vas zdaj vpraša vsakič.',
    modeAutomaticSet: '{app} to zdaj naredi brez vprašanja.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Dovoljeno brez vprašanja',
    allowedOf: '{allowed} od {total}',
    allowedNone: 'Še nobenega',
    allowedActions: 'one:dejanje|two:dejanji|few:dejanja|other:dejanj',
    groupReadOnly: 'Nastavljeno na Branje, zato se nič ne spremeni',
    groupSendingAsks: 'Pošiljanje iz Gmaila in Outlooka še vedno vpraša vas',
    groupOff: 'Izklopljeno',
    groupNothingToChange: 'Ni česa spreminjati',
    groupUnavailable: 'Nalaganje ni uspelo',
    showAll: 'Pokaži vseh {count} dejanj v {name}',
    showFewer: 'Pokaži manj',
    loading: 'Nalaganje dejanj …',
    loadFailed: 'Teh dejanj ni bilo mogoče naložiti.',
    retry: 'Poskusi znova',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Mogoče je razveljaviti.',
    lockedNote: 'Vedno vpraša vas.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Nekatere stvari vedno vprašajo vas, karkoli izberete: prenos lastništva podjetja, odstranitev člana, objava Aplikacije, porabljanje denarja onkraj kreditov, potrditev kode Aplikacije, ki jo je napisal {app}, ter pošiljanje iz Gmaila ali Outlooka. Dovoljenje dejanja {app} nikoli ne da več, kot imate vi, povezava, nastavljena na Branje, pa ostane samo za branje.',
    errScopeNotGranted: '{app} te dostope nima več. Seznam prikazuje trenutno stanje.',
    errNotAllowable: 'Tega dejanja ni mogoče dovoliti brez vprašanja.',
    errReadOnly: 'Ta povezava je nastavljena na Branje, zato na njej ni mogoče ničesar dovoliti.',
    errNotSaved: 'Ni bilo shranjeno. Nič se ni spremenilo.',
    errNotConfirmed: 'Spremembe ni bilo mogoče potrditi. Seznam prikazuje trenutno stanje.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Shrani predstavitev v Knjižnico',
    action_save_document: 'Shrani dokument v Knjižnico',
    action_save_table: 'Shrani tabelo v Knjižnico',
    action_save_chart: 'Shrani grafikon v Knjižnico',
    action_save_page: 'Shrani stran v Knjižnico',
    action_add_to_poneva_files: 'Dodaj datoteko med datoteke Poneva',
    action_save_poneva_skill_draft: 'Shrani osnutek Skilla',
    action_import_agent_skill: 'Uvozi Skill',
    action_evaluate_poneva_skill: 'Ovrednoti Skill',
    action_ask_poneva: 'Vprašaj Poneva v zasebnem klepetu',
    action_continue_in_poneva: 'Nadaljuj v klepetu Poneva',
    action_design_shopping_assistant: 'Shrani zasnovo nakupovalnega pomočnika',
    action_storefront_preview_turn: 'Predogled odgovora nakupovalnega pomočnika',
    action_test_shopping_assistant: 'Preizkusi nakupovalnega pomočnika z vprašanjem',
    action_fix_shopping_assistant: 'Popravi nakupovalnega pomočnika',
    action_check_shopping_assistant_again: 'Znova preveri nakupovalnega pomočnika',
    action_build_poneva_app: 'Zgradi Live App',
    action_delete_poneva_app: 'Izbriši Live App',
    action_update_item_sharing: 'Spremeni, s kom je element deljen',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Porablja kredite.',
    note_storefront_preview_turn: 'Porablja kredite.',
    note_test_shopping_assistant: 'Porablja kredite.',
    note_fix_shopping_assistant: 'Porablja kredite.',
    note_check_shopping_assistant_again: 'Porablja kredite.',
    note_update_item_sharing: 'Vidijo ga takoj. Deljenje lahko prekinete.',
    note_delete_poneva_app: 'Ni mogoče razveljaviti.',
  },
  sv: {
    // The Use your connections switch (data:connections), mirroring the Connections switch copy.
    connTitle: 'Använd dina anslutningar',
    connDescribe:
      'Slack, Pipedream-appar, egen MCP och Postgres. Ändringar sker bara på en anslutning som är inställd på Läsning och skrivning.',
    connConfirmOn:
      'Låta {app} använda {company}s anslutningar som dig? Allt som ändrar något väntar på dig i Inkorgen om du inte tillåter det.',
    connConfirmOff: 'Sluta låta {app} använda dina anslutningar?',
    connEnabled: '{app} kan nu använda dina anslutningar.',
    connDisabled: '{app} kan inte längre använda dina anslutningar.',
    // The Read live stores (stores:read) and Media monitoring (media:read) switches.
    storesTitle: 'Läs butiker live',
    storesDescribe:
      'Aktuella priser, lagerstatus och produktdetaljer från riktiga webbutiker, med Ponevas certifierade butiksverktyg. Varje läsning kostar krediter.',
    storesConfirmOn:
      'Låta {app} läsa butiker live som dig? Varje läsning använder {company}s krediter.',
    storesConfirmOff: 'Sluta låta {app} läsa butiker live?',
    storesEnabled: '{app} kan nu läsa butiker live.',
    storesDisabled: '{app} kan inte längre läsa butiker live.',
    mediaTitle: 'Mediebevakning',
    mediaDescribe:
      'Sök och citera det som sades i de poddar och radioprogram som Ponevas mediearkiv samlar in.',
    mediaConfirmOn: 'Låta {app} söka i {company}s mediebevakning som dig?',
    mediaConfirmOff: 'Sluta låta {app} söka i mediebevakningen?',
    mediaEnabled: '{app} kan nu söka i mediebevakningen.',
    mediaDisabled: '{app} kan inte längre söka i mediebevakningen.',
    // The two modes of a permission family.
    modeAsk: 'Fråga varje gång',
    modeAutomatic: 'Automatiskt',
    modeGroupLabel: '{family}: godkännande',
    askExplain: 'Varje åtgärd väntar på dig i Inkorgen, om du inte tillåter den nedan.',
    automaticSave:
      '{app} sparar arbete och frågar Poneva utan att fråga dig, även sådant som inte kan ångras.',
    automaticConnections:
      '{app} använder dina anslutningar utan att fråga dig, även för ändringar på anslutningar inställda på Läsning och skrivning. Utskick från Gmail och Outlook frågar fortfarande dig.',
    confirmAutomatic: 'Låta {app} göra detta utan att fråga dig först?',
    modeAskSet: '{app} frågar dig nu varje gång.',
    modeAutomaticSet: '{app} gör nu detta utan att fråga.',
    // The Allowed without asking well and its counts.
    allowedTitle: 'Tillåtet utan att fråga',
    allowedOf: '{allowed} av {total}',
    allowedNone: 'Inga ännu',
    allowedActions: 'one:åtgärd|other:åtgärder',
    groupReadOnly: 'Inställd på Läsning, så inget ändras',
    groupSendingAsks: 'Utskick från Gmail och Outlook frågar fortfarande dig',
    groupOff: 'Avstängd',
    groupNothingToChange: 'Inget att ändra',
    groupUnavailable: 'Kunde inte läsas in',
    showAll: 'Visa alla {count} åtgärder i {name}',
    showFewer: 'Visa färre',
    loading: 'Läser in åtgärder …',
    loadFailed: 'Åtgärderna kunde inte läsas in.',
    retry: 'Försök igen',
    // Notes under an action; the server sends the action flags.
    undoableNote: 'Kan ångras.',
    lockedNote: 'Frågar alltid dig.',
    // The card note and plain-words 409 messages.
    floorNote:
      'Vissa saker frågar alltid dig, oavsett vad du väljer: överföring av företagets ägande, borttagning av en medlem, att göra en App offentlig, att spendera pengar utöver krediter, att bekräfta App-kod som {app} har skrivit samt utskick från Gmail eller Outlook. Att tillåta en åtgärd ger aldrig {app} mer än du har, och en anslutning inställd på Läsning förblir skrivskyddad.',
    errScopeNotGranted: '{app} har inte längre denna åtkomst. Listan visar aktuellt läge.',
    errNotAllowable: 'Den åtgärden kan inte tillåtas utan att fråga.',
    errReadOnly: 'Den anslutningen är inställd på Läsning, så inget på den kan tillåtas.',
    errNotSaved: 'Det sparades inte. Inget ändrades.',
    errNotConfirmed: 'Ändringen kunde inte bekräftas. Listan visar aktuellt läge.',
    // Native action labels, keyed by tool name.
    action_save_deck: 'Spara en presentation i ditt Bibliotek',
    action_save_document: 'Spara ett dokument i ditt Bibliotek',
    action_save_table: 'Spara en tabell i ditt Bibliotek',
    action_save_chart: 'Spara ett diagram i ditt Bibliotek',
    action_save_page: 'Spara en sida i ditt Bibliotek',
    action_add_to_poneva_files: 'Lägg till en fil bland dina Poneva-filer',
    action_save_poneva_skill_draft: 'Spara ett utkast till en Skill',
    action_import_agent_skill: 'Importera en Skill',
    action_evaluate_poneva_skill: 'Utvärdera en Skill',
    action_ask_poneva: 'Fråga Poneva i en privat chatt',
    action_continue_in_poneva: 'Fortsätt i en Poneva-chatt',
    action_design_shopping_assistant: 'Spara en design för en shoppingassistent',
    action_storefront_preview_turn: 'Förhandsgranska en shoppingassistents svar',
    action_test_shopping_assistant: 'Testa en shoppingassistent med en fråga',
    action_fix_shopping_assistant: 'Åtgärda en shoppingassistent',
    action_check_shopping_assistant_again: 'Kontrollera en shoppingassistent igen',
    action_build_poneva_app: 'Bygg en Live App',
    action_delete_poneva_app: 'Ta bort en Live App',
    action_update_item_sharing: 'Ändra vem ett objekt delas med',
    // Per-action notes where a flag cannot say it.
    note_ask_poneva: 'Använder krediter.',
    note_storefront_preview_turn: 'Använder krediter.',
    note_test_shopping_assistant: 'Använder krediter.',
    note_fix_shopping_assistant: 'Använder krediter.',
    note_check_shopping_assistant_again: 'Använder krediter.',
    note_update_item_sharing: 'De ser det direkt. Du kan sluta dela.',
    note_delete_poneva_app: 'Kan inte ångras.',
  },
};

const languages = [
  'en',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
] as const;

const copy = Object.fromEntries(
  languages.map((language) => [language, language === 'en' ? { ...en } : { ...others[language] }]),
) as NamespaceCatalog<AgentPermissionsCopyKey>;

export default copy;
